import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Toast from './Toast';
import ImageCel from './cells/image_cel';
import MediaPickerModal from './MediaPickerModal';
import ListCel, { ListEntry } from './cells/list_cel';
import ListEditor from './list-editor';
import QuickAddPopup from './QuickAddPopup';
import HeadingOverlay from './overlays/HeadingOverlay';
import ButtonOverlay from './overlays/ButtonOverlay';
import CellOverlayListPopup2, { type OverlayListItem } from './CellOverlayListPopup2';
import CellOverlaySettingsPopup, { type HeadingSettings, type ButtonSettings } from './CellOverlaySettingsPopup';

export type PageBlock = {
  id: string;
  type: 'text' | 'image' | 'hero' | 'cta' | 'listV' | 'listH';
  x: number; // grid column start (1..12)
  y: number; // row start (1..n)
  w: number; // columns span
  h: number; // rows span (approx height units)
  content?: string; // for text/cta
  url?: string; // for image/hero background image
  // Optional image pan offsets (pixels) for 'image' blocks
  imgOffsetX?: number;
  imgOffsetY?: number;
  // Optional image zoom scale relative to cover scale (1 = cover)
  imgScale?: number;
  // Optional list items for list blocks
  listItems?: string[];
  // Optional rich list entries with per-item images
  listEntries?: ListEntry[];
  // List image presentation options
  listImageSize?: number; // px
  listImageRadius?: number; // px
};

export type PageModel = {
  title: string;
  blocks: PageBlock[];
  rows?: number; // number of grid rows
  columns?: number; // number of grid columns
  gapX?: number; // column gap in px
  gapY?: number; // row gap in px
  radius?: number; // border radius in px
  colFractions?: number[]; // optional custom fractions per column, sum ~ 1
  rowFractions?: number[]; // optional custom fractions per row, sum ~ 1
  colGaps?: number[]; // optional per-divider column gaps (length columns-1)
  rowGaps?: number[]; // optional per-divider row gaps (length rows-1)
  merges?: MergeRect[]; // merged cell rectangles
  layoutHeight?: number; // absolute total layout height in px for deterministic row height reconstruction
  rowHeightsPx?: number[]; // absolute row heights used to render; takes precedence over rowFractions when present
  // Optional desired total section height for previews (e.g., enforce aspect ratio)
  targetHeightPx?: number;
  // Per-cell style persistence (keys are "r:c")
  padByCell?: Record<string, number>;
  keepRadiusByCell?: Record<string, boolean>;
  customRadiusByCell?: Record<string, number>;
  customRadiusEnabledByCell?: Record<string, boolean>;
  cellEdgeRadiusByCell?: Record<string, number>;
  cellEdgeRadiusEnabledByCell?: Record<string, boolean>;
  // Overlays stored per cell so previews can render them
  headingByCell?: Record<string, { id: string; text?: string; align?: 'left'|'center'|'right'; color?: string; shadow?: boolean; x?: number; y?: number }[]>;
  buttonByCell?: Record<string, { id: string; label?: string; variant?: 'primary'|'secondary'; x?: number; y?: number }[]>;
};

// Per-cell merge rectangles (spans do not alter track counts)
export type MergeRect = { r: number; c: number; rs: number; cs: number };

type Props = {
  page: PageModel;
  columns?: number; // default columns if not in page
  rows?: number; // default rows if not in page
  editable?: boolean;
  onDropNew?: (type: PageBlock['type'], x: number, y: number) => void;
  onMoveBlock?: (id: string, x: number, y: number) => void;
  onGridSizesChange?: (cols: number[], rows: number[], layoutHeight: number, rowHeightsPx: number[]) => void;
  showInnerHelpers?: boolean;
  onGridGapsChange?: (colGaps: number[], rowGaps: number[]) => void;
  showGapHandles?: boolean;
  onGridMergesChange?: (merges: MergeRect[]) => void;
  beginHistoryGroup?: () => void;
  commitHistoryGroup?: () => void;
  onUpdateBlock?: (id: string, patch: Partial<PageBlock>) => void;
  onRemoveBlock?: (id: string) => void;
  compact?: boolean;
  // Persist per-cell styles to parent (editor should store on section)
  onCellStylesChange?: (patch: {
    padByCell?: Record<string, number>;
    keepRadiusByCell?: Record<string, boolean>;
    customRadiusByCell?: Record<string, number>;
    customRadiusEnabledByCell?: Record<string, boolean>;
    cellEdgeRadiusByCell?: Record<string, number>;
  }) => void;
};

const DisplayPage: React.FC<Props> = ({ page, columns = 12, rows = 12, editable = false, onDropNew, onMoveBlock, onGridSizesChange, showInnerHelpers = true, onGridGapsChange, showGapHandles = true, onGridMergesChange, beginHistoryGroup, commitHistoryGroup, onUpdateBlock, onRemoveBlock, compact = false, onCellStylesChange }) => {
  const cols = page.columns || columns;
  const rws = page.rows || rows;
  const gx = page.gapX ?? 16;
  const gy = page.gapY ?? 16;
  const rad = page.radius ?? 12;
  const [pad, setPad] = useState<number>(5);
  const [padByCell, setPadByCell] = useState<Record<string, number>>({});
  const [settingsFor, setSettingsFor] = useState<{ r: number; c: number; pos?: { top: number; left: number } } | null>(null);
  const [quickAddFor, setQuickAddFor] = useState<{ r: number; c: number; pos?: { top: number; left: number } } | null>(null);
  const [keepRadiusByCell, setKeepRadiusByCell] = useState<Record<string, boolean>>({});
  const [customRadiusByCell, setCustomRadiusByCell] = useState<Record<string, number>>({});
  const [customRadiusEnabledByCell, setCustomRadiusEnabledByCell] = useState<Record<string, boolean>>({});
  // Per-cell overlays (prototype): heading by cell key "r:c"
  const newOverlayId = () => Math.random().toString(36).slice(2, 10);
  type HeadingCfg = { id: string; text?: string; align?: 'left'|'center'|'right'; color?: string; shadow?: boolean; x?: number; y?: number };
  const [headingByCell, setHeadingByCell] = useState<Record<string, HeadingCfg[]>>({});
  const headingDragRef = useRef<{ key: string; id: string; startX: number; startY: number; baseX: number; baseY: number; maxX: number; maxY: number } | null>(null);
  const onHeadingDown = (e: React.MouseEvent, key: string, id: string) => {
    if (!editable) return;
    e.preventDefault(); e.stopPropagation();
    const target = e.currentTarget as HTMLDivElement;
    const parent = target.parentElement as HTMLDivElement | null; // the absolute inset-0 container
    const pbW = parent ? parent.clientWidth : 0;
    const pbH = parent ? parent.clientHeight : 0;
    const tbW = target.offsetWidth;
    const tbH = target.offsetHeight;
    const raw = headingByCell[key] as any;
    const arr: HeadingCfg[] = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const cur = arr.find(o => o.id === id) || ({} as any);
    const baseX = Math.max(0, Math.floor(cur.x || 12));
    const baseY = Math.max(0, Math.floor(cur.y || 12));
    const maxX = Math.max(0, Math.floor(pbW - tbW));
    const maxY = Math.max(0, Math.floor(pbH - tbH));
    headingDragRef.current = { key, id, startX: e.clientX, startY: e.clientY, baseX, baseY, maxX, maxY };
    window.addEventListener('mousemove', onHeadingMove);
    window.addEventListener('mouseup', onHeadingUp, { once: true });
  };
  const onHeadingMove = (e: MouseEvent) => {
    const d = headingDragRef.current; if (!d) return;
    const dx = e.clientX - d.startX; const dy = e.clientY - d.startY;
    const nx = Math.max(0, Math.min(d.maxX, d.baseX + dx));
    const ny = Math.max(0, Math.min(d.maxY, d.baseY + dy));
    setHeadingByCell(prev => {
      const raw = (prev as any)[d.key];
      const arr: HeadingCfg[] = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      const next = arr.map(o => o.id === d.id ? { ...o, x: nx, y: ny } : o);
      const out = { ...prev, [d.key]: next } as any;
      emitCellStyles({ headingByCell: out as any });
      return out;
    });
  };
  const onHeadingUp = () => {
    window.removeEventListener('mousemove', onHeadingMove);
    headingDragRef.current = null;
  };
  // Button overlays per cell
  type ButtonCfg = {
    id: string;
    label?: string;
    // visuals
    textColor?: string;
    bgMode?: 'solid'|'gradient';
    bgColor?: string;
    gradFrom?: string;
    gradTo?: string;
    radius?: number;
    shadow?: boolean;
    height?: number;
    paddingX?: number;
    fontSize?: number;
    glow?: boolean;
    outlineColor?: string;
    outlineWidth?: number;
    capsule?: boolean;
    outlineMode?: 'color'|'gradient';
    outlineGradFrom?: string;
    outlineGradTo?: string;
    x?: number; y?: number;
  };
  const [buttonByCell, setButtonByCell] = useState<Record<string, ButtonCfg[]>>({});
  const buttonDragRef = useRef<{ key: string; id: string; startX: number; startY: number; baseX: number; baseY: number; maxX: number; maxY: number } | null>(null);
  const onButtonDown = (e: React.MouseEvent, key: string, id: string) => {
    if (!editable) return;
    e.preventDefault(); e.stopPropagation();
    const target = e.currentTarget as HTMLDivElement;
    const parent = target.parentElement as HTMLDivElement | null;
    const pbW = parent ? parent.clientWidth : 0; const pbH = parent ? parent.clientHeight : 0;
    const tbW = target.offsetWidth; const tbH = target.offsetHeight;
    const raw = (buttonByCell as any)[key]; const arr: ButtonCfg[] = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const cur = arr.find(o => o.id === id) || ({} as any);
    const baseX = Math.max(0, Math.floor(cur.x || 12));
    const baseY = Math.max(0, Math.floor(cur.y || 12));
    const maxX = Math.max(0, Math.floor(pbW - tbW));
    const maxY = Math.max(0, Math.floor(pbH - tbH));
    buttonDragRef.current = { key, id, startX: e.clientX, startY: e.clientY, baseX, baseY, maxX, maxY };
    window.addEventListener('mousemove', onButtonMove);
    window.addEventListener('mouseup', onButtonUp, { once: true });
  };
  const onButtonMove = (e: MouseEvent) => {
    const d = buttonDragRef.current; if (!d) return;
    const dx = e.clientX - d.startX; const dy = e.clientY - d.startY;
    const nx = Math.max(0, Math.min(d.maxX, d.baseX + dx));
    const ny = Math.max(0, Math.min(d.maxY, d.baseY + dy));
    setButtonByCell(prev => {
      const raw = (prev as any)[d.key]; const arr: ButtonCfg[] = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      const next = arr.map(o => o.id === d.id ? { ...o, x: nx, y: ny } : o);
      const out = { ...prev, [d.key]: next } as any;
      emitCellStyles({ buttonByCell: out as any });
      return out;
    });
  };
  const onButtonUp = () => {
    window.removeEventListener('mousemove', onButtonMove);
    buttonDragRef.current = null;
  };
  // Per-cell edge radius override (overrides row/column/base for that specific cell)
  const [cellEdgeRadiusByCell, setCellEdgeRadiusByCell] = useState<Record<string, number>>({});
  const [cellEdgeRadiusEnabledByCell, setCellEdgeRadiusEnabledByCell] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; ts: number } | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const settingsAnchorRef = useRef<HTMLElement | null>(null);
  const settingsPanelRef = useRef<HTMLDivElement | null>(null);
  const quickAddAnchorRef = useRef<HTMLElement | null>(null);
  const listAnchorRef = useRef<HTMLElement | null>(null);
  const overlaySettingsAnchorRef = useRef<HTMLElement | null>(null);
  const overlaySettingsPanelRef = useRef<HTMLDivElement | null>(null);
  // Row/Column settings state
  const [rowRadiusByIndex, setRowRadiusByIndex] = useState<Record<number, number>>({});
  const [colRadiusByIndex, setColRadiusByIndex] = useState<Record<number, number>>({});
  // Timestamps to implement "last change wins"
  const [rowRadiusTsByIndex, setRowRadiusTsByIndex] = useState<Record<number, number>>({});
  const [colRadiusTsByIndex, setColRadiusTsByIndex] = useState<Record<number, number>>({});
  const [trackSettingsFor, setTrackSettingsFor] = useState<{ type: 'row'|'col'; index: number; x: number; y: number } | null>(null);
  const [mediaPickerFor, setMediaPickerFor] = useState<{ id: string } | null>(null);
  const [listEditorFor, setListEditorFor] = useState<{ id: string; orientation: 'vertical'|'horizontal' } | null>(null);
  // Image adjust (pan) state
  const [adjustingImage, setAdjustingImage] = useState<{ id: string } | null>(null);
  const [tempImgOffsets, setTempImgOffsets] = useState<Record<string, { x: number; y: number }>>({});
  const [imgNaturalSizes, setImgNaturalSizes] = useState<Record<string, { w: number; h: number }>>({});
  const [tempImgScale, setTempImgScale] = useState<Record<string, number>>({});
  const imgDragRef = useRef<{ id: string; startX: number; startY: number; baseX: number; baseY: number; boxW: number; boxH: number; rendW: number; rendH: number } | null>(null);
  const onImgPanDown = (e: React.MouseEvent, id: string, baseX: number, baseY: number, boxW: number, boxH: number, rendW: number, rendH: number) => {
    if (!editable || !adjustingImage || adjustingImage.id !== id) return;
    e.preventDefault(); e.stopPropagation();
    imgDragRef.current = { id, startX: e.clientX, startY: e.clientY, baseX, baseY, boxW, boxH, rendW, rendH };
    window.addEventListener('mousemove', onImgPanMove);
    window.addEventListener('mouseup', onImgPanUp, { once: true });
  };
  const onImgPanMove = (e: MouseEvent) => {
    const d = imgDragRef.current; if (!d) return;
    const dx = e.clientX - d.startX; const dy = e.clientY - d.startY;
    let nx = (d.baseX || 0) + dx; let ny = (d.baseY || 0) + dy;
    // Clamp using rendered cover overflow so background never reveals.
    const maxX = Math.max(0, (d.rendW - d.boxW) / 2);
    const maxY = Math.max(0, (d.rendH - d.boxH) / 2);
    nx = Math.min(maxX, Math.max(-maxX, nx));
    ny = Math.min(maxY, Math.max(-maxY, ny));
    setTempImgOffsets(prev => ({ ...prev, [d.id]: { x: nx, y: ny } }));
  };
  const onImgPanUp = () => {
    window.removeEventListener('mousemove', onImgPanMove);
    imgDragRef.current = null;
  };

  // Keep the cell settings popup positioned near its anchor during scroll/resize, fully within viewport
  useEffect(() => {
    if (!settingsFor || !settingsAnchorRef.current) return;
    const panelW = 224; const gap = 8;
    const update = () => {
      const el = settingsAnchorRef.current; if (!el) return;
      const rect = el.getBoundingClientRect();
      const panelH = settingsPanelRef.current?.getBoundingClientRect().height || 0;
      const leftRaw = rect.right - panelW;
      const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw));
      let top = rect.bottom + gap;
      // If bottom overflows and we have panel height, try place above; else clamp within viewport
      const maxTop = Math.max(8, window.innerHeight - (panelH || 0) - 8);
      if (panelH && (top + panelH > window.innerHeight - 8)) {
        const above = rect.top - gap - panelH;
        top = above >= 8 ? above : Math.min(maxTop, Math.max(8, top));
      } else {
        top = Math.min(maxTop, Math.max(8, top));
      }
      setSettingsFor(s => (s && s.r === settingsFor.r && s.c === settingsFor.c)
        ? { ...s, pos: { top, left } } : s);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [settingsFor?.r, settingsFor?.c]);

  useEffect(() => {
    if (!quickAddFor || !quickAddAnchorRef.current) return;
    const panelW = 256; const gap = 8;
    const update = () => {
      const el = quickAddAnchorRef.current; if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.bottom + gap;
      const leftRaw = rect.right - panelW;
      const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw));
      setQuickAddFor(s => (s && s.r === quickAddFor.r && s.c === quickAddFor.c)
        ? { ...s, pos: { top, left } } : s);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [quickAddFor?.r, quickAddFor?.c]);

  const [listFor, setListFor] = useState<{ r: number; c: number; pos?: { top: number; left: number } } | null>(null);
  useEffect(() => {
    if (!listFor || !listAnchorRef.current) return;
    const panelW = 256; const gap = 8;
    const update = () => {
      const el = listAnchorRef.current; if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.bottom + gap;
      const leftRaw = rect.right - panelW;
      const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw));
      setListFor(s => (s && s.r === listFor.r && s.c === listFor.c)
        ? { ...s, pos: { top, left } } : s);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [listFor?.r, listFor?.c]);

  // Per-item settings popup state
  const [overlaySettingsFor, setOverlaySettingsFor] = useState<{ r: number; c: number; kind: 'heading'|'button'; id: string; pos?: { top:number; left:number } }|null>(null);
  useEffect(() => {
    if (!overlaySettingsFor || !overlaySettingsAnchorRef.current) return;
    const panelW = 288; const gap = 8;
    const update = () => {
      const el = overlaySettingsAnchorRef.current; if (!el) return;
      const rect = el.getBoundingClientRect();
      const panelH = overlaySettingsPanelRef.current?.offsetHeight ?? 360;
      // Default place below anchor
      let top = rect.bottom + gap;
      let leftRaw = rect.right - panelW;
      let left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw));
      // If bottom would overflow, flip above
      if (top + panelH > window.innerHeight - 8) {
        top = Math.max(8, rect.top - panelH - gap);
      }
      // Final clamp
      top = Math.max(8, Math.min(window.innerHeight - panelH - 8, top));
      left = Math.max(8, Math.min(window.innerWidth - panelW - 8, left));
      setOverlaySettingsFor(s => (s && s.id===overlaySettingsFor.id && s.r===overlaySettingsFor.r && s.c===overlaySettingsFor.c)
        ? { ...s, pos: { top, left } } : s);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [overlaySettingsFor?.id, overlaySettingsFor?.r, overlaySettingsFor?.c]);

  const removeCellBlock = (id: string) => {
    if (!onRemoveBlock) return;
    if (beginHistoryGroup) beginHistoryGroup();
    onRemoveBlock(id);
    if (adjustingImage && adjustingImage.id === id) setAdjustingImage(null);
    if (listEditorFor && listEditorFor.id === id) setListEditorFor(null);
    setSettingsFor(null);
    showToast('Removed from cell');
    if (commitHistoryGroup) commitHistoryGroup();
  };
  // Per-divider gaps
  const [colGaps, setColGaps] = useState<number[]>(() => (page.colGaps && page.colGaps.length === Math.max(0, cols - 1)) ? page.colGaps : Array.from({ length: Math.max(0, cols - 1) }, () => gx));
  const [rowGaps, setRowGaps] = useState<number[]>(() => (page.rowGaps && page.rowGaps.length === Math.max(0, rws - 1)) ? page.rowGaps : Array.from({ length: Math.max(0, rws - 1) }, () => gy));
  const colGapsRef = useRef<number[]>(colGaps);
  const rowGapsRef = useRef<number[]>(rowGaps);
  const [colFracs, setColFracs] = useState<number[]>(() => {
    const src = page.colFractions && page.colFractions.length === cols ? page.colFractions : Array.from({length: cols}, () => 1/cols);
    const s = src.reduce((a,b)=>a+b,0) || 1; return src.map(v=>v/s);
  });
  const [rowFracs, setRowFracs] = useState<number[]>(() => {
    const src = page.rowFractions && page.rowFractions.length === rws ? page.rowFractions : Array.from({length: rws}, () => 1/rws);
    const s = src.reduce((a,b)=>a+b,0) || 1; return src.map(v=>v/s);
  });
  // Pixel-based heights used for live layout so rows below can shift down (total height can grow)
  const [rowHeights, setRowHeights] = useState<number[]>(() => {
    if (page.rowHeightsPx && page.rowHeightsPx.length === rws) {
      return page.rowHeightsPx.map(h => Math.max(64, h));
    }
    const baseTotal = page.layoutHeight ?? (rws * 128);
    const fr = (page.rowFractions && page.rowFractions.length === rws) ? page.rowFractions : Array.from({length: rws}, () => 1/rws);
    const s = fr.reduce((a,b)=>a+b,0) || 1; const norm = fr.map(v=>v/s);
    const arr = norm.map(v => Math.max(64, v * baseTotal));
    return arr;
  });
  const colFracsRef = useRef<number[]>(colFracs);
  const rowFracsRef = useRef<number[]>(rowFracs);
  const rowHeightsRef = useRef<number[]>([]);

  useEffect(() => { colFracsRef.current = colFracs; }, [colFracs]);
  useEffect(() => { rowFracsRef.current = rowFracs; }, [rowFracs]);
  useEffect(() => { rowHeightsRef.current = rowHeights; }, [rowHeights]);
  useEffect(() => { colGapsRef.current = colGaps; }, [colGaps]);
  useEffect(() => { rowGapsRef.current = rowGaps; }, [rowGaps]);

  useEffect(() => {
    // Helper: proportional resampler
    const resizeFracs = (src: number[] | undefined, newLen: number): number[] => {
      const n = Math.max(1, Math.floor(newLen));
      const a = (src && src.length ? norm(src) : Array.from({ length: n }, () => 1 / n));
      if (a.length === n) return a;
      const result: number[] = [];
      const ratio = a.length / n;
      for (let i = 0; i < n; i++) {
        const start = i * ratio;
        const end = (i + 1) * ratio;
        let sum = 0;
        for (let j = Math.floor(start); j < Math.ceil(end); j++) {
          const left = Math.max(start, j);
          const right = Math.min(end, j + 1);
          const weight = Math.max(0, right - left);
          sum += (a[j] || 0) * weight;
        }
        result.push(sum);
      }
      const s = result.reduce((x, y) => x + y, 0) || 1;
      return result.map(v => Math.max(0.01, v / s));
    };

  // Donor carve from a left neighbor that vertically covers the band [start..end) and is at column (col-1)
  const carveFromLeftDonor = (ms: MergeRect[], col: number, bandStart: number, bandLen: number): boolean => {
    if (col <= 0) return false;
    const bandEnd = bandStart + bandLen;
    // donor: rect at col-1 whose vertical coverage includes entire band
    const donorIdx = ms.findIndex(m => (m.c === col - 1) && (m.r <= bandStart) && (m.r + m.rs) >= bandEnd && m.cs >= 1);
    if (donorIdx === -1) return false;
    // recipients: rects in column 'col' that tile exactly the band
    const recipients = ms
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.c === col && m.cs === 1 && m.r >= bandStart && (m.r + m.rs) <= bandEnd)
      .sort((a, b) => a.m.r - b.m.r);
    if (recipients.length === 0) return false;
    let cur = bandStart;
    for (const { m } of recipients) {
      if (m.r !== cur) return false; // gap
      cur += m.rs;
    }
    if (cur !== bandEnd) return false; // not full cover
    // Apply: shrink donor by 1 column from the right, expand each recipient left by 1 (shift c-1, cs+1)
    const donor = ms[donorIdx];
    donor.cs -= 1;
    if (donor.cs === 0) { ms.splice(donorIdx, 1); }
    // Important: if donorIdx < recipient indices, their indices shift when donor removed
    for (const rec of recipients) {
      const m = ms[donorIdx === -1 ? rec.i : (rec.i > donorIdx && donor.cs === 0 ? rec.i - 1 : rec.i)];
      m.c = m.c - 1; m.cs = m.cs + 1;
    }
    return true;
  };

    setColFracs(prev => {
      if (page.colFractions && page.colFractions.length) {
        return norm(page.colFractions);
      }
      // When no explicit fractions are provided, default to equal fractions
      // so undo to a state without custom fractions restores the default layout.
      return Array.from({ length: cols }, () => 1 / Math.max(1, cols));
    });
    setRowFracs(prev => {
      if (page.rowHeightsPx && page.rowHeightsPx.length === rws) {
        const total = page.rowHeightsPx.reduce((a,b)=>a+b,0) || 1;
        return page.rowHeightsPx.map(h => Math.max(0.01, h / total));
      }
      if (page.rowFractions && page.rowFractions.length) {
        return norm(page.rowFractions);
      }
      if (prev.length !== rws) return resizeFracs(prev, rws);
      return prev;
    });
    // Sync pixel heights: if exact heights provided, use them; else derive from fractions/total
    setRowHeights(prev => {
      const MIN = 64;
      if (page.rowHeightsPx && page.rowHeightsPx.length === rws) {
        return page.rowHeightsPx.map(h => Math.max(MIN, h));
      }
      const total = (page.layoutHeight && page.layoutHeight > 0)
        ? page.layoutHeight
        : ((prev && prev.length ? prev.reduce((a,b)=>a+b,0) : 0) || rws * 128);
      const fr = (page.rowFractions && page.rowFractions.length === rws)
        ? norm(page.rowFractions)
        : Array.from({ length: rws }, () => 1 / rws);
      const arr = fr.map(v => Math.max(MIN, v * total));
      return arr;
    });
    // Also ensure gaps arrays match track counts based on provided fractions length (fallback to defaults)
    setColGaps(prev => {
      const desiredCols = (page.colFractions && page.colFractions.length) ? page.colFractions.length : cols;
      const need = Math.max(0, desiredCols - 1);
      if (page.colGaps && page.colGaps.length === need) return page.colGaps;
      if (prev.length !== need) return Array.from({ length: need }, (_, i) => (page.colGaps && page.colGaps[i] != null) ? page.colGaps[i]! : gx);
      return prev;
    });
    setRowGaps(prev => {
      const desiredRows = (page.rowFractions && page.rowFractions.length) ? page.rowFractions.length : rws;
      const need = Math.max(0, desiredRows - 1);
      if (page.rowGaps && page.rowGaps.length === need) return page.rowGaps;
      if (prev.length !== need) return Array.from({ length: need }, (_, i) => (page.rowGaps && page.rowGaps[i] != null) ? page.rowGaps[i]! : gy);
      return prev;
    });
  }, [cols, rws, page.colFractions, page.rowFractions, page.colGaps, page.rowGaps, page.rowHeightsPx, page.layoutHeight, gx, gy]);

  function getPad(r: number, c: number) {
    const key = `${r}:${c}`;
    return padByCell[key] ?? pad;
  }
  function getKeepRadius(r: number, c: number) {
    const key = `${r}:${c}`;
    return !!keepRadiusByCell[key];
  }
  function getCustomRadius(r: number, c: number) {
    const key = `${r}:${c}`; return customRadiusByCell[key];
  }
  function isCustomRadiusEnabled(r: number, c: number) {
    const key = `${r}:${c}`; return !!customRadiusEnabledByCell[key];
  }
  function getCellEdgeRadius(r: number, c: number) {
    const key = `${r}:${c}`; return cellEdgeRadiusByCell[key];
  }
  function isCellEdgeRadiusEnabled(r: number, c: number) {
    const key = `${r}:${c}`; return !!cellEdgeRadiusEnabledByCell[key];
  }
  const [cellEdgeRadiusTsByCell, setCellEdgeRadiusTsByCell] = useState<Record<string, number>>({});

  // Sync initial per-cell styles from page model so non-editable views render identically
  useEffect(() => {
    if (page.padByCell) setPadByCell(page.padByCell);
    if (page.keepRadiusByCell) setKeepRadiusByCell(page.keepRadiusByCell);
    if (page.customRadiusByCell) setCustomRadiusByCell(page.customRadiusByCell);
    if (page.customRadiusEnabledByCell) setCustomRadiusEnabledByCell(page.customRadiusEnabledByCell);
    if (page.cellEdgeRadiusByCell) setCellEdgeRadiusByCell(page.cellEdgeRadiusByCell);
    if ((page as any).cellEdgeRadiusEnabledByCell) setCellEdgeRadiusEnabledByCell((page as any).cellEdgeRadiusEnabledByCell);
    if ((page as any).headingByCell) setHeadingByCell((page as any).headingByCell as any);
    if ((page as any).buttonByCell) setButtonByCell((page as any).buttonByCell as any);
  }, [page.padByCell, page.keepRadiusByCell, page.customRadiusByCell, page.customRadiusEnabledByCell, page.cellEdgeRadiusByCell, (page as any).headingByCell, (page as any).buttonByCell]);

  const emitCellStyles = (next?: Partial<{
    padByCell: Record<string, number>;
    keepRadiusByCell: Record<string, boolean>;
    customRadiusByCell: Record<string, number>;
    customRadiusEnabledByCell: Record<string, boolean>;
    cellEdgeRadiusByCell: Record<string, number>;
    headingByCell: Record<string, HeadingCfg[]>;
    buttonByCell: Record<string, ButtonCfg[]>;
    cellEdgeRadiusEnabledByCell: Record<string, boolean>;
  }>) => {
    if (!onCellStylesChange) return;
    onCellStylesChange({
      padByCell,
      keepRadiusByCell,
      customRadiusByCell,
      customRadiusEnabledByCell,
      cellEdgeRadiusByCell,
      headingByCell,
      buttonByCell,
      cellEdgeRadiusEnabledByCell,
      ...(next || {})
    });
  };
  const showToast = (msg: string, timeout = 2200) => {
    setToast({ msg, ts: Date.now() });
    if (toastTimerRef.current) { window.clearTimeout(toastTimerRef.current); }
    toastTimerRef.current = window.setTimeout(() => { setToast(null); toastTimerRef.current = null; }, timeout);
  };

  // Padding edge drag to adjust inner space
  const padDragRef = useRef<{
    edge: 'left'|'right'|'top'|'bottom';
    startX: number;
    startY: number;
    base: number;
    key: string;
    r: number;
    c: number;
    spanR: number;
    spanC: number;
  }|null>(null);
  const onPadDown = (e: React.MouseEvent, edge: 'left'|'right'|'top'|'bottom', r: number, c: number, spanR: number, spanC: number) => {
    if (!editable) return;
    e.preventDefault(); e.stopPropagation();
    const key = `${r}:${c}`;
    const base = getPad(r, c);
    padDragRef.current = { edge, startX: e.clientX, startY: e.clientY, base, key, r, c, spanR, spanC };
    window.addEventListener('mousemove', onPadMove);
    window.addEventListener('mouseup', onPadUp, { once: true });
  };
  const onPadMove = (e: MouseEvent) => {
    const d = padDragRef.current; if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    let delta = 0;
    // Make movement intuitive: moving inward increases padding.
    if (d.edge === 'left') delta = dx;       // right -> increase, left -> decrease
    if (d.edge === 'right') delta = -dx;     // left -> increase, right -> decrease
    if (d.edge === 'top') delta = dy;        // down -> increase, up -> decrease
    if (d.edge === 'bottom') delta = -dy;    // up -> increase, down -> decrease
    const width = (colTrackWidths.slice(d.c, d.c + d.spanC).reduce((a,b)=>a+b,0)) || 0;
    const height = (rowHeights.slice(d.r, d.r + d.spanR).reduce((a,b)=>a+b,0)) || 0;
    const maxPad = Math.max(0, Math.floor(Math.min(width, height) / 2) - 2);
    const next = Math.max(0, Math.min(maxPad, Math.round(d.base + delta)));
    setPadByCell(prev => { const out = { ...prev, [d.key]: next }; emitCellStyles({ padByCell: out }); return out; });
  };
  const onPadUp = () => {
    window.removeEventListener('mousemove', onPadMove);
    padDragRef.current = null;
  };

  function norm(arr: number[]) { const s = arr.reduce((a,b)=>a+b,0)||1; return arr.map(v=>Math.max(0.01, v/s)); }

  // Build template with interleaved content and gap tracks so each divider can have its own size
  const templateCols = useMemo(() => {
    const parts: string[] = [];
    for (let i = 0; i < colFracs.length; i++) {
      parts.push(`${Math.max(0.0001, colFracs[i]).toFixed(6)}fr`);
      if (i < colFracs.length - 1) parts.push(`${(colGaps[i] ?? gx).toFixed(0)}px`);
    }
    return parts.join(' ');
  }, [colFracs, colGaps, gx]);
  const templateRows = useMemo(() => {
    const parts: string[] = [];
    for (let i = 0; i < rowHeights.length; i++) {
      parts.push(`${rowHeights[i].toFixed(2)}px`);
      if (i < rowHeights.length - 1) parts.push(`${(rowGaps[i] ?? gy).toFixed(0)}px`);
    }
    return parts.join(' ');
  }, [rowHeights, rowGaps, gy]);
  const visCols = colFracs.length;
  const visRows = rowHeights.length;
  // Total grid height (content rows + gaps) for overlay sizing
  const totalGridHeight = useMemo(() => {
    const rowsSum = rowHeights.reduce((a,b)=>a+b,0) || 0;
    const gapsSum = rowGaps.reduce((a,b)=>a+b,0) || 0;
    return rowsSum + gapsSum;
  }, [rowHeights, rowGaps]);

  // Per-cell merged rectangles
  const [merges, setMerges] = useState<MergeRect[]>([]);
  // Top-level sync: update merges from page when provided or when grid size changes
  useEffect(() => {
    const vr = visRows; const vc = visCols;
    const src = page.merges || [];
    const clamped: MergeRect[] = src.map(m => ({
      r: Math.max(0, Math.min(m.r, vr - 1)),
      c: Math.max(0, Math.min(m.c, vc - 1)),
      rs: Math.max(1, Math.min(m.rs, Math.max(1, vr - Math.max(0, Math.min(m.r, vr - 1))))),
      cs: Math.max(1, Math.min(m.cs, Math.max(1, vc - Math.max(0, Math.min(m.c, vc - 1)))))
    }));
    setMerges(clamped);
  }, [page.merges, visRows, visCols]);
  const covers = (rect: MergeRect, rr: number, cc: number) => rr >= rect.r && rr < rect.r + rect.rs && cc >= rect.c && cc < rect.c + rect.cs;
  const findOwner = (rr: number, cc: number) => merges.findIndex(m => covers(m, rr, cc));
  const collides = (probe: MergeRect, ignoreIdx: number = -1) => merges.some((m, i) => i !== ignoreIdx && !(probe.r + probe.rs <= m.r || m.r + m.rs <= probe.r || probe.c + probe.cs <= m.c || m.c + m.cs <= probe.c));
  const clampRect = (r: number, c: number, rs: number, cs: number): MergeRect => ({ r: Math.max(0, Math.min(r, visRows - 1)), c: Math.max(0, Math.min(c, visCols - 1)), rs: Math.max(1, Math.min(rs, visRows - r)), cs: Math.max(1, Math.min(cs, visCols - c)) });
  const rectAt = (ms: MergeRect[], rr: number, cc: number): { idx: number; rect: MergeRect } => {
    const idx = ms.findIndex(m => covers(m, rr, cc));
    return idx === -1 ? { idx: -1, rect: { r: rr, c: cc, rs: 1, cs: 1 } } : { idx, rect: ms[idx] };
  };
  const touchingRight = (ms: MergeRect[], a: MergeRect, selfIdx: number) => ms.findIndex((m, i) => i !== selfIdx && m.c === a.c + a.cs && m.r === a.r && m.rs === a.rs);
  const touchingLeft = (ms: MergeRect[], a: MergeRect, selfIdx: number) => ms.findIndex((m, i) => i !== selfIdx && (m.c + m.cs) === a.c && m.r === a.r && m.rs === a.rs);
  const touchingDown = (ms: MergeRect[], a: MergeRect, selfIdx: number) => ms.findIndex((m, i) => i !== selfIdx && m.r === a.r + a.rs && m.c === a.c && m.cs === a.cs);
  const touchingUp = (ms: MergeRect[], a: MergeRect, selfIdx: number) => ms.findIndex((m, i) => i !== selfIdx && (m.r + m.rs) === a.r && m.c === a.c && m.cs === a.cs);
  // Helpers to check if a band is fully covered by stacked rects of width/height 1
  const columnBandCovered = (ms: MergeRect[], band: MergeRect, colIndex: number): number[] | null => {
    // collect rects exactly in next column that lie within band vertically
    const rects = ms
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.c === colIndex && m.cs === 1 && m.r >= band.r && (m.r + m.rs) <= (band.r + band.rs))
      .sort((a, b) => a.m.r - b.m.r);
    if (rects.length === 0) return null;
    // verify they tile the band with no gaps
    let cur = band.r;
    for (const { m } of rects) {
      if (m.r !== cur) return null; // gap
      cur += m.rs;
    }
    if (cur !== band.r + band.rs) return null; // not full cover
    return rects.map(({ i }) => i);
  };

  // Globally scoped: donor carve from a left neighbor that vertically covers the band
  const carveFromLeftDonorEx = (ms: MergeRect[], col: number, bandStart: number, bandLen: number): boolean => {
    if (col <= 0) return false;
    const bandEnd = bandStart + bandLen;
    const donorIdx = ms.findIndex(m => (m.c === col - 1) && (m.r <= bandStart) && (m.r + m.rs) >= bandEnd && m.cs >= 1);
    if (donorIdx === -1) return false;
    const recipients = ms
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.c === col && m.cs === 1 && m.r >= bandStart && (m.r + m.rs) <= bandEnd)
      .sort((a, b) => a.m.r - b.m.r);
    if (recipients.length === 0) return false;
    let cur = bandStart;
    for (const { m } of recipients) { if (m.r !== cur) return false; cur += m.rs; }
    if (cur !== bandEnd) return false;
    const donor = ms[donorIdx];
    donor.cs -= 1;
    if (donor.cs === 0) { ms.splice(donorIdx, 1); }
    for (const rec of recipients) {
      const index = (donor.cs === 0 && rec.i > donorIdx) ? rec.i - 1 : rec.i;
      const m = ms[index]; m.c -= 1; m.cs += 1;
    }
    return true;
  };
  const rowBandCovered = (ms: MergeRect[], band: MergeRect, rowIndex: number): number[] | null => {
    const rects = ms
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.r === rowIndex && m.rs === 1 && m.c >= band.c && (m.c + m.cs) <= (band.c + band.cs))
      .sort((a, b) => a.m.c - b.m.c);
    if (rects.length === 0) return null;
    let cur = band.c;
    for (const { m } of rects) {
      if (m.c !== cur) return null;
      cur += m.cs;
    }
    if (cur !== band.c + band.cs) return null;
    return rects.map(({ i }) => i);
  };
  // Expand/merge utilities using latest state
  const notifyMerges = (ms: MergeRect[]) => { if (onGridMergesChange) onGridMergesChange(ms.map(m => ({ ...m }))); };

  // Precompute which rows/columns are impacted by any merged cell so we can hide controls there
  const rowsBlockedByMerge = useMemo(() => {
    const arr = Array.from({ length: visRows }, () => false);
    for (const m of merges) {
      if (m.rs > 1 || m.cs > 1) {
        for (let rr = m.r; rr < m.r + m.rs && rr < visRows; rr++) arr[rr] = true;
      }
    }
    return arr;
  }, [merges, visRows]);
  const colsBlockedByMerge = useMemo(() => {
    const arr = Array.from({ length: visCols }, () => false);
    for (const m of merges) {
      if (m.rs > 1 || m.cs > 1) {
        for (let cc = m.c; cc < m.c + m.cs && cc < visCols; cc++) arr[cc] = true;
      }
    }
    return arr;
  }, [merges, visCols]);

  const expandRight = (r: number, c: number) => {
    setMerges(prev => {
      const ms = [...prev];
      let { idx, rect: a } = rectAt(ms, r, c);
      if (idx === -1) { ms.push(a); idx = ms.length - 1; }
      // Try merge with rect directly to the right with equal height
      const bIdx = touchingRight(ms, a, idx);
      if (bIdx !== -1) {
        const b = ms[bIdx];
        ms[idx] = { r: a.r, c: a.c, rs: a.rs, cs: a.cs + b.cs };
        ms.splice(bIdx, 1);
        notifyMerges(ms);
        return ms;
      }
      // If the immediate next column is tiled by stacked rects over this band's height, consume all of them, then expand
      const coverIdxs = columnBandCovered(ms, a, a.c + a.cs);
      if (coverIdxs && coverIdxs.length) {
        // remove in reverse to keep indices valid
        coverIdxs.sort((x, y) => y - x).forEach(i2 => ms.splice(i2, 1));
        ms[idx] = { ...a, cs: a.cs + 1 };
        notifyMerges(ms);
        return ms;
      }
      // Otherwise expand by one if it doesn't collide
      const next: MergeRect = clampRect(a.r, a.c, a.rs, Math.min(visCols - a.c, a.cs + 1));
      if (!ms.some((m, i) => i !== idx && !(next.r + next.rs <= m.r || m.r + m.rs <= next.r || next.c + next.cs <= m.c || m.c + m.cs <= next.c))) ms[idx] = next;
      notifyMerges(ms);
      return ms;
    });
  };
  const expandLeft = (r: number, c: number) => {
    setMerges(prev => {
      const ms = [...prev];
      let { idx, rect: a } = rectAt(ms, r, c);
      if (idx === -1) { ms.push(a); idx = ms.length - 1; }
      // Special case: stacked recipients in this column, donor big rect on the left covering full vertical band
      if (carveFromLeftDonorEx(ms, a.c, a.r, a.rs)) {
        notifyMerges(ms);
        return ms;
      }
      const bIdx = touchingLeft(ms, a, idx);
      if (bIdx !== -1) {
        const b = ms[bIdx];
        ms[idx] = { r: b.r, c: b.c, rs: a.rs, cs: b.cs + a.cs };
        ms.splice(bIdx, 1);
        notifyMerges(ms);
        return ms;
      }
      const coverIdxs = columnBandCovered(ms, a, a.c - 1);
      if (coverIdxs && coverIdxs.length) {
        coverIdxs.sort((x, y) => y - x).forEach(i2 => ms.splice(i2, 1));
        ms[idx] = { r: a.r, c: a.c - 1, rs: a.rs, cs: a.cs + 1 };
        notifyMerges(ms);
        return ms;
      }
      const newC = Math.max(0, a.c - 1);
      const delta = a.c - newC;
      const next: MergeRect = clampRect(a.r, newC, a.rs, a.cs + delta);
      if (!ms.some((m, i) => i !== idx && !(next.r + next.rs <= m.r || m.r + m.rs <= next.r || next.c + next.cs <= m.c || m.c + m.cs <= next.c))) ms[idx] = next;
      notifyMerges(ms);
      return ms;
    });
  };
  const expandDown = (r: number, c: number) => {
    setMerges(prev => {
      const ms = [...prev];
      let { idx, rect: a } = rectAt(ms, r, c);
      if (idx === -1) { ms.push(a); idx = ms.length - 1; }
      const bIdx = touchingDown(ms, a, idx);
      if (bIdx !== -1) {
        const b = ms[bIdx];
        ms[idx] = { r: a.r, c: a.c, rs: a.rs + b.rs, cs: a.cs };
        ms.splice(bIdx, 1);
        notifyMerges(ms);
        return ms;
      }
      const coverIdxs = rowBandCovered(ms, a, a.r + a.rs);
      if (coverIdxs && coverIdxs.length) {
        coverIdxs.sort((x, y) => y - x).forEach(i2 => ms.splice(i2, 1));
        ms[idx] = { ...a, rs: a.rs + 1 };
        notifyMerges(ms);
        return ms;
      }
      const next: MergeRect = clampRect(a.r, a.c, Math.min(visRows - a.r, a.rs + 1), a.cs);
      if (!ms.some((m, i) => i !== idx && !(next.r + next.rs <= m.r || m.r + m.rs <= next.r || next.c + next.cs <= m.c || m.c + m.cs <= next.c))) ms[idx] = next;
      notifyMerges(ms);
      return ms;
    });
  };
  const expandUp = (r: number, c: number) => {
    setMerges(prev => {
      const ms = [...prev];
      let { idx, rect: a } = rectAt(ms, r, c);
      if (idx === -1) { ms.push(a); idx = ms.length - 1; }
      const bIdx = touchingUp(ms, a, idx);
      if (bIdx !== -1) {
        const b = ms[bIdx];
        ms[idx] = { r: b.r, c: a.c, rs: b.rs + a.rs, cs: a.cs };
        ms.splice(bIdx, 1);
        notifyMerges(ms);
        return ms;
      }
      const coverIdxs = rowBandCovered(ms, a, a.r - 1);
      if (coverIdxs && coverIdxs.length) {
        coverIdxs.sort((x, y) => y - x).forEach(i2 => ms.splice(i2, 1));
        ms[idx] = { r: a.r - 1, c: a.c, rs: a.rs + 1, cs: a.cs };
        notifyMerges(ms);
        return ms;
      }
      const newR = Math.max(0, a.r - 1);
      const delta = a.r - newR;
      const next: MergeRect = clampRect(newR, a.c, a.rs + delta, a.cs);
      if (!ms.some((m, i) => i !== idx && !(next.r + next.rs <= m.r || m.r + m.rs <= next.r || next.c + next.cs <= m.c || m.c + m.cs <= next.c))) ms[idx] = next;
      notifyMerges(ms);
      return ms;
    });
  };

  // Drag state
  const [scaleMode, setScaleMode] = useState<{ type: 'col'|'row'; index: number } | null>(null);
  const dragRef = useRef<{ type: 'col'|'row'; index: number; start: number; colFracs: number[]; rowFracs: number[]; rowHeights: number[] }|null>(null);
  const onDragStartLine = (e: React.MouseEvent, type: 'col'|'row', index: number) => {
    if (!editable) return;
    const start = type === 'col' ? e.clientX : e.clientY;
    dragRef.current = { type, index, start, colFracs: [...colFracs], rowFracs: [...rowFracs], rowHeights: [...rowHeights] };
    if (beginHistoryGroup) beginHistoryGroup();
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd, { once: true });
  };
  const onDragMove = (e: MouseEvent) => {
    const d = dragRef.current; if (!d) return;
    const box = gridRef.current?.getBoundingClientRect(); if (!box) return;
    if (d.type === 'col') {
      const dx = e.clientX - d.start;
      const width = box.width; if (width <= 0) return;
      const minF = 0.05;
      const delta = dx / width;
      const newCols = [...d.colFracs];
      if (scaleMode && scaleMode.type === 'col' && scaleMode.index === d.index) {
        const leftCount = d.index + 1;
        const rightCount = newCols.length - leftCount;
        const sumLeft = newCols.slice(0, leftCount).reduce((a,b)=>a+b,0);
        const sumRight = 1 - sumLeft;
        const minLeft = Math.max(leftCount * minF, 0);
        const minRight = Math.max(rightCount * minF, 0);
        let targetLeft = Math.min(1 - minRight, Math.max(minLeft, sumLeft + delta));
        const targetRight = 1 - targetLeft;
        // Scale groups proportionally
        const left = newCols.slice(0, leftCount);
        const right = newCols.slice(leftCount);
        const norm = (arr: number[], total: number) => {
          const s = arr.reduce((a,b)=>a+b,0) || 1;
          return arr.map(v => Math.max(minF, v / s * total));
        };
        const newLeft = norm(left, targetLeft);
        const newRight = norm(right, targetRight);
        for (let i = 0; i < leftCount; i++) newCols[i] = newLeft[i];
        for (let i = 0; i < right.length; i++) newCols[leftCount + i] = newRight[i];
      } else {
        // Default: adjust only the two adjacent columns around the divider
        const lf = d.colFracs[d.index];
        const rf = d.colFracs[d.index+1];
        const total = lf + rf;
        let nf = Math.min(total - minF, Math.max(minF, lf + delta));
        newCols[d.index] = nf;
        newCols[d.index+1] = total - nf;
      }
      setColFracs(newCols);
      colFracsRef.current = newCols;
    } else {
      const dy = e.clientY - d.start;
      const i = d.index;
      const MIN_ROW_PX = 64;
      const baseHeights = [...d.rowHeights];
      const newHeights = [...baseHeights];
      if (scaleMode && scaleMode.type === 'row' && scaleMode.index === i) {
        // Scale all rows above proportionally, borrowing from below group
        const aboveCount = i + 1;
        const belowCount = newHeights.length - aboveCount;
        const minH = MIN_ROW_PX;
        const sumAbove = newHeights.slice(0, aboveCount).reduce((a,b)=>a+b,0);
        const sumBelow = newHeights.slice(aboveCount).reduce((a,b)=>a+b,0);
        let targetAbove = Math.max(aboveCount*minH, sumAbove + dy);
        // Ensure below rows not below min
        const minBelowTotal = belowCount * minH;
        const totalAll = sumAbove + sumBelow;
        targetAbove = Math.min(totalAll - minBelowTotal, targetAbove);
        const targetBelow = totalAll - targetAbove;
        const scaleGroup = (arr: number[], total: number) => {
          const s = arr.reduce((a,b)=>a+b,0) || 1; return arr.map(v => Math.max(minH, v / s * total));
        };
        const newAbove = scaleGroup(newHeights.slice(0, aboveCount), targetAbove);
        const newBelow = scaleGroup(newHeights.slice(aboveCount), targetBelow);
        const combo = [...newAbove, ...newBelow];
        setRowHeights(combo);
        rowHeightsRef.current = combo;
        const tot = combo.reduce((a,b)=>a+b,0)||1; const fr = combo.map(h=>h/tot);
        setRowFracs(fr); rowFracsRef.current = fr;
      } else {
        newHeights[i] = Math.max(MIN_ROW_PX, baseHeights[i] + dy);
        setRowHeights(newHeights);
        rowHeightsRef.current = newHeights;
        // Keep fractions in sync for helper positions
        const total = newHeights.reduce((a,b)=>a+b,0) || 1;
        const fr = newHeights.map(h => Math.max(0.01, h / total));
        setRowFracs(fr);
        rowFracsRef.current = fr;
      }
    }
  };
  const onDragEnd = () => {
    window.removeEventListener('mousemove', onDragMove);
    const d = dragRef.current; dragRef.current = null;
    if (onGridSizesChange && d) {
      const heights = rowHeightsRef.current;
      const total = heights.reduce((a,b)=>a+b,0)||0;
      onGridSizesChange(colFracsRef.current, rowFracsRef.current, total, heights);
    }
    if (commitHistoryGroup) commitHistoryGroup();
  };

  // Split a merged rectangle at cell (r,c) back into single cells by removing the covering merge
  const splitMergeAt = (r: number, c: number) => {
    setMerges(prev => {
      const ms = [...prev];
      const idx = ms.findIndex(m => covers(m, r, c));
      if (idx === -1) return ms; // nothing to split
      const owner = ms[idx];
      const filtered = ms.filter((m, i) => !(m.r >= owner.r && m.c >= owner.c && (m.r + m.rs) <= (owner.r + owner.rs) && (m.c + m.cs) <= (owner.c + owner.cs)));
      notifyMerges(filtered);
      return filtered;
    });
  };

  // Track vertical helper positions (px) computed from actual grid track sizes
  const [vLineLefts, setVLineLefts] = useState<number[]>([]);
  const [colTrackWidths, setColTrackWidths] = useState<number[]>([]);
  const [colGapStarts, setColGapStarts] = useState<number[]>([]);
  const [colGapWidthsComputed, setColGapWidthsComputed] = useState<number[]>([]);
  const [rowGapStarts, setRowGapStarts] = useState<number[]>([]);
  const [rowGapHeightsComputed, setRowGapHeightsComputed] = useState<number[]>([]);
  const [hGapCenters, setHGapCenters] = useState<number[]>([]);
  useEffect(() => {
    const el = gridRef.current; if (!el) return;
    const update = () => {
      const desiredCols = colFracs.length;
      const desiredRows = rowHeights.length;
      if (desiredCols <= 1) { setVLineLefts([]); setColTrackWidths([]); setColGapStarts([]); setColGapWidthsComputed([]); };
      const cs = getComputedStyle(el);
      // Parse computed tracks (content and gaps interleaved)
      const cTracks = cs.gridTemplateColumns.split(' ').map(v => parseFloat(v)).filter(n => !Number.isNaN(n));
      const rTracks = cs.gridTemplateRows.split(' ').map(v => parseFloat(v)).filter(n => !Number.isNaN(n));
      // Extract content widths (even indices) and gap widths (odd indices)
      const contentCols: number[] = []; const gapCols: number[] = [];
      for (let i = 0; i < cTracks.length; i++) { (i % 2 === 0 ? contentCols : gapCols).push(cTracks[i]); }
      const contentRows: number[] = []; const gapRows: number[] = [];
      for (let i = 0; i < rTracks.length; i++) { (i % 2 === 0 ? contentRows : gapRows).push(rTracks[i]); }
      setColTrackWidths(contentCols.slice(0, desiredCols));
      // Compute gap starts and centers for columns
      const colStarts: number[] = [];
      const colCenters: number[] = [];
      let cumC = 0;
      for (let i = 0; i < desiredCols - 1; i++) {
        cumC += contentCols[i] || 0; // after content i
        const w = gapCols[i] || 0;
        colStarts.push(cumC);
        // Use precise center without rounding so the dot does not snap
        colCenters.push(cumC + w / 2);
        cumC += w;
      }
      setColGapStarts(colStarts);
      setColGapWidthsComputed(gapCols.slice(0, Math.max(0, desiredCols - 1)));
      setVLineLefts(colCenters);

      // Compute gap starts and centers for rows
      const rowStarts: number[] = [];
      const rowCenters: number[] = [];
      let cumR = 0;
      for (let j = 0; j < desiredRows - 1; j++) {
        cumR += contentRows[j] || 0; // after row j
        const h = gapRows[j] || 0;
        rowStarts.push(cumR);
        // Use precise center without rounding so the dot does not snap
        rowCenters.push(cumR + h / 2);
        cumR += h;
      }
      setRowGapStarts(rowStarts);
      setRowGapHeightsComputed(gapRows.slice(0, Math.max(0, desiredRows - 1)));
      setHGapCenters(rowCenters);
    };
    update();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    }
    window.addEventListener('resize', update);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', update); };
  }, [colFracs, rowHeights, templateCols, templateRows]);

  // Horizontal helper centers are computed from actual grid tracks -> hGapCenters
  // Total grid width for row overlay sizing (depends on measured tracks)
  const totalGridWidth = useMemo(() => {
    const colsSum = colTrackWidths.reduce((a,b)=>a+b,0) || 0;
    const gapsSum = colGaps.reduce((a,b)=>a+b,0) || 0;
    return colsSum + gapsSum;
  }, [colTrackWidths, colGaps]);

  // Compute effective corner radius for a block based on per-cell override, then row/column overrides, else base
  const effectiveRadiusForBlock = (b: PageBlock): number => {
    // 1) Per-cell override (takes absolute precedence when enabled for any covered cell)
    const rStart = Math.max(0, (b.y || 1) - 1);
    const rEnd = rStart + Math.max(1, b.h || 1) - 1;
    const cStart = Math.max(0, (b.x || 1) - 1);
    const cEnd = cStart + Math.max(1, b.w || 1) - 1;
    let cellOverrideFound = false;
    let cellOverrideVal = 0;
    for (let rr = rStart; rr <= rEnd; rr++) {
      for (let cc = cStart; cc <= cEnd; cc++) {
        const key = `${rr}:${cc}`;
        const enabled = !!cellEdgeRadiusEnabledByCell[key];
        const v = cellEdgeRadiusByCell[key];
        if (enabled && typeof v === 'number') {
          cellOverrideFound = true;
          cellOverrideVal = Math.max(cellOverrideVal, Math.max(0, v));
        }
      }
    }
    if (cellOverrideFound) return cellOverrideVal;

    // 2) Rows override columns entirely. If any row in span has a value, use the
    // max row value and ignore column values. Else, use max column value. Else, base.
    let foundRow = false;
    let rowEff = 0;
    for (let r = rStart; r <= rEnd && r < rowHeights.length; r++) {
      const val = rowRadiusByIndex[r];
      if (typeof val === 'number') { foundRow = true; rowEff = Math.max(rowEff, val); }
    }
    if (foundRow) return Math.max(0, rowEff);

    let foundCol = false;
    let colEff = 0;
    for (let c = cStart; c <= cEnd && c < colFracs.length; c++) {
      const val = colRadiusByIndex[c];
      if (typeof val === 'number') { foundCol = true; colEff = Math.max(colEff, val); }
    }
    if (foundCol) return Math.max(0, colEff);

    return Math.max(0, rad);
  };

  // Insert a new row at the horizontal helper gap index j (between row j and j+1)
  const insertRowAtGap = (gapIndex: number) => {
    const j = Math.max(0, Math.min(gapIndex, rowHeightsRef.current.length - 1));
    const DEFAULT_H = 96; // standard new row height
    const nextHeights = [...rowHeightsRef.current];
    nextHeights.splice(j + 1, 0, DEFAULT_H);
    // Duplicate the gap value at j to keep visual spacing consistent
    const nextRowGaps = [...rowGapsRef.current];
    const dupGap = nextRowGaps[j] != null ? nextRowGaps[j] : gy;
    nextRowGaps.splice(j, 0, dupGap);
    setRowGaps(nextRowGaps);
    // Apply heights (updates fractions and notifies parent)
    if (beginHistoryGroup) beginHistoryGroup();
    applyRowHeights(nextHeights);
    if (commitHistoryGroup) commitHistoryGroup();
  };

  // Insert a new column at the vertical helper gap index i (between col i and i+1)
  const insertColAtGap = (gapIndex: number) => {
    const MINF = 0.05;
    const i = Math.max(0, Math.min(gapIndex, colFracsRef.current.length - 1));
    const base = [...colFracsRef.current];
    // Prefer splitting the right neighbor to keep local total width stable
    const rightIdx = Math.min(i + 1, base.length - 1);
    const target = base[rightIdx] ?? (1 / Math.max(1, base.length));
    const half = Math.max(MINF, target / 2);
    base[rightIdx] = half;
    base.splice(rightIdx, 0, half);
    // Duplicate gap at i to keep spacing consistent
    const nextGaps = [...colGapsRef.current];
    const dup = nextGaps[i] != null ? nextGaps[i] : gx;
    nextGaps.splice(i, 0, dup);
    setColGaps(nextGaps);
    if (beginHistoryGroup) beginHistoryGroup();
    applyColFracs(base);
    if (commitHistoryGroup) commitHistoryGroup();
  };

  // Arrow actions: grow cell by consuming neighbors
  const MINF = 0.05;
  const MINH = 64;
  const applyRowHeights = (heights: number[]) => {
    setRowHeights(heights);
    rowHeightsRef.current = heights;
    const tot = heights.reduce((a,b)=>a+b,0)||1; const fr = heights.map(h=>Math.max(0.01, h/tot));
    setRowFracs(fr); rowFracsRef.current = fr;
    if (onGridSizesChange) onGridSizesChange(colFracsRef.current, fr, heights.reduce((a,b)=>a+b,0)||0, heights);
  };
  const applyColFracs = (fracs: number[]) => {
    const s = fracs.reduce((a,b)=>a+b,0)||1; const normed = fracs.map(v=>Math.max(MINF, v/s));
    setColFracs(normed); colFracsRef.current = normed;
    const heights = rowHeightsRef.current;
    if (onGridSizesChange) onGridSizesChange(normed, rowFracsRef.current, heights.reduce((a,b)=>a+b,0)||0, heights);
  };

  // Gap drag handles
  const gapDragRef = useRef<{ kind: 'col'|'row'; index: number; start: number; base: number; colGaps: number[]; rowGaps: number[] }|null>(null);
  const onGapHandleDown = (e: React.MouseEvent, kind: 'col'|'row', index: number) => {
    if (!editable) return;
    e.preventDefault(); e.stopPropagation();
    const start = kind === 'col' ? e.clientX : e.clientY;
    gapDragRef.current = { kind, index, start, base: kind==='col' ? (colGaps[index]||gx) : (rowGaps[index]||gy), colGaps: [...colGaps], rowGaps: [...rowGaps] };
    if (beginHistoryGroup) beginHistoryGroup();
    window.addEventListener('mousemove', onGapDragMove);
    window.addEventListener('mouseup', onGapDragEnd, { once: true });
  };
  const onGapDragMove = (e: MouseEvent) => {
    const d = gapDragRef.current; if (!d) return;
    const delta = d.kind === 'col' ? (e.clientX - d.start) : (e.clientY - d.start);
    const MIN = 0; const MAX = 96;
    if (d.kind === 'col') {
      const next = [...d.colGaps];
      next[d.index] = Math.max(MIN, Math.min(MAX, d.base + delta));
      setColGaps(next);
    } else {
      const next = [...d.rowGaps];
      next[d.index] = Math.max(MIN, Math.min(MAX, d.base + delta));
      setRowGaps(next);
    }
  };
  const onGapDragEnd = () => {
    window.removeEventListener('mousemove', onGapDragMove);
    const d = gapDragRef.current; gapDragRef.current = null;
    if (onGridGapsChange) onGridGapsChange(colGapsRef.current, rowGapsRef.current);
    if (commitHistoryGroup) commitHistoryGroup();
  };
  const growRowDown = (r: number) => {
    if (r < 0 || r >= rws - 1) return;
    const h = [...rowHeights];
    let takeFrom = r + 1;
    let gained = 0;
    while (takeFrom < h.length) {
      const slack = Math.max(0, h[takeFrom] - MINH);
      if (slack <= 0) { takeFrom++; continue; }
      const take = slack; // consume all slack of this row
      h[takeFrom] -= take; gained += take; break; // single-row consume for one click
    }
    if (gained > 0) { h[r] += gained; applyRowHeights(h); }
  };
  const growRowUp = (r: number) => {
    if (r <= 0) return;
    const h = [...rowHeights];
    let takeFrom = r - 1; let gained = 0;
    while (takeFrom >= 0) {
      const slack = Math.max(0, h[takeFrom] - MINH);
      if (slack <= 0) { takeFrom--; continue; }
      const take = slack; h[takeFrom] -= take; gained += take; break;
    }
    if (gained > 0) { h[r] += gained; applyRowHeights(h); }
  };
  const growColRight = (c: number) => {
    if (c < 0 || c >= cols - 1) return;
    const f = [...colFracsRef.current];
    let takeFrom = c + 1; let gained = 0;
    while (takeFrom < f.length) {
      const slack = Math.max(0, f[takeFrom] - MINF);
      if (slack <= 0) { takeFrom++; continue; }
      const take = slack; f[takeFrom] -= take; gained += take; break;
    }
    if (gained > 0) { f[c] += gained; applyColFracs(f); }
  };
  const growColLeft = (c: number) => {
    if (c <= 0) return;
    const f = [...colFracsRef.current];
    let takeFrom = c - 1; let gained = 0;
    while (takeFrom >= 0) {
      const slack = Math.max(0, f[takeFrom] - MINF);
      if (slack <= 0) { takeFrom--; continue; }
      const take = slack; f[takeFrom] -= take; gained += take; break;
    }
    if (gained > 0) { f[c] += gained; applyColFracs(f); }
  };

  // Merge helpers: remove divider and combine sizes
  const mergeRowDown = (r: number) => {
    if (r < 0 || r >= rowHeights.length - 1) return;
    const h = [...rowHeights];
    h[r] = h[r] + h[r+1];
    h.splice(r+1, 1);
    applyRowHeights(h);
  };
  const mergeRowUp = (r: number) => {
    if (r <= 0) return;
    const h = [...rowHeights];
    h[r-1] = h[r-1] + h[r];
    h.splice(r, 1);
    applyRowHeights(h);
  };
  const mergeColRight = (c: number) => {
    if (c < 0 || c >= colFracs.length - 1) return;
    const f = [...colFracsRef.current];
    f[c] = f[c] + f[c+1];
    f.splice(c+1, 1);
    applyColFracs(f);
  };
  const mergeColLeft = (c: number) => {
    if (c <= 0) return;
    const f = [...colFracsRef.current];
    f[c-1] = f[c-1] + f[c];
    f.splice(c, 1);
    applyColFracs(f);
  };

  // Delete helpers: remove a specific row/column by merging into a neighbor
  const deleteRowAt = (r: number) => {
    if (r < 0 || r >= rowHeights.length) return;
    if (beginHistoryGroup) beginHistoryGroup();
    if (r === 0) mergeRowDown(0); else mergeRowUp(r);
    if (commitHistoryGroup) commitHistoryGroup();
  };
  const deleteColAt = (c: number) => {
    if (c < 0 || c >= colFracs.length) return;
    if (beginHistoryGroup) beginHistoryGroup();
    if (c === 0) mergeColRight(0); else mergeColLeft(c);
    if (commitHistoryGroup) commitHistoryGroup();
  };

  // Dot dragging: move both a vertical and a horizontal divider at once
  const dotDragRef = useRef<{
    startX: number; startY: number; i: number; j: number;
    colFracs: number[]; rowHeights: number[];
  }|null>(null);
  const onDotDown = (e: React.MouseEvent, i: number, j: number) => {
    if (!editable) return;
    e.preventDefault(); e.stopPropagation();
    dotDragRef.current = {
      startX: e.clientX, startY: e.clientY, i, j,
      colFracs: [...colFracs], rowHeights: [...rowHeights]
    };
    if (beginHistoryGroup) beginHistoryGroup();
    window.addEventListener('mousemove', onDotMove);
    window.addEventListener('mouseup', onDotUp, { once: true });
  };
  const onDotMove = (e: MouseEvent) => {
    const d = dotDragRef.current; if (!d) return;
    const box = gridRef.current?.getBoundingClientRect(); if (!box) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // Columns: adjust adjacent pair i / i+1 using fraction delta
    const width = box.width; if (width <= 0) return;
    const MINF = 0.05;
    const newCols = [...d.colFracs];
    const lf = d.colFracs[d.i]; const rf = d.colFracs[d.i+1];
    const total = lf + rf;
    let nf = Math.min(total - MINF, Math.max(MINF, lf + dx / width));
    newCols[d.i] = nf; newCols[d.i+1] = total - nf;
    setColFracs(newCols); colFracsRef.current = newCols;
    // Rows: adjust vertical divider precisely. Borrow slack from below when moving down.
    const MINH = 64;
    const base = [...d.rowHeights];
    const newRows = [...base];
    const j = d.j;
    if (j >= 0 && j < newRows.length - 1) {
      // Apply dy directly to row j and compensate from row j+1 using base snapshot, clamped to MINH.
      // This allows total height to grow/shrink if neighbor cannot fully compensate, so movement never gets stuck.
      const up = Math.max(MINH, base[j] + dy);
      const downNeighbor = Math.max(MINH, base[j+1] - dy);
      newRows[j] = up;
      newRows[j+1] = downNeighbor;
    } else {
      // No gap below: just adjust the row itself within bounds
      newRows[j] = Math.max(MINH, base[j] + dy);
    }
    setRowHeights(newRows);
    rowHeightsRef.current = newRows;
    const sum = newRows.reduce((a,b)=>a+b,0)||1; const fr = newRows.map(h=>Math.max(0.01, h/sum));
    setRowFracs(fr); rowFracsRef.current = fr;
  };
  const onDotUp = () => {
    window.removeEventListener('mousemove', onDotMove);
    dotDragRef.current = null;
    // Persist latest via onGridSizesChange by reusing onDragEnd path
    if (onGridSizesChange) {
      const heights = rowHeightsRef.current;
      const total = heights.reduce((a,b)=>a+b,0)||0;
      onGridSizesChange(colFracsRef.current, rowFracsRef.current, total, heights);
    }
    if (commitHistoryGroup) commitHistoryGroup();
  };
  
  // Track Ctrl (Strg) and Shift key state and hovered cell for highlight
  const [ctrlDown, setCtrlDown] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);
  const [hoverCell, setHoverCell] = useState<{ r: number; c: number } | null>(null);
  const [hoveredDot, setHoveredDot] = useState<{ i: number; j: number; x: number; y: number } | null>(null);
  // Simple context menu for '+' actions
  const [menuPos, setMenuPos] = useState<{ x: number; y: number; i: number; j: number } | null>(null);
  useEffect(() => {
    const onDocDown = () => setMenuPos(null);
    if (menuPos) {
      window.addEventListener('mousedown', onDocDown, { once: true });
    }
    return () => { window.removeEventListener('mousedown', onDocDown); };
  }, [menuPos]);
  // While a dot is considered hovered, hide buttons if pointer moves >20px from dot center
  useEffect(() => {
    if (!hoveredDot) return;
    const onMove = (e: MouseEvent) => {
      const box = gridRef.current?.getBoundingClientRect();
      if (!box) return;
      const relX = e.clientX - box.left;
      const relY = e.clientY - box.top;
      const dx = relX - hoveredDot.x;
      const dy = relY - hoveredDot.y;
      if (Math.hypot(dx, dy) > 20) setHoveredDot(null);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [hoveredDot]);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.ctrlKey) setCtrlDown(true);
      if (e.key === 'Shift' || e.shiftKey) setShiftDown(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || !e.ctrlKey) setCtrlDown(false);
      if (e.key === 'Shift' || !e.shiftKey) setShiftDown(false);
    };
    const onBlur = () => { setCtrlDown(false); setShiftDown(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  return (
    <div className="w-full">
      <div className={compact ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {!compact && (
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">{page.title}</h1>
        )}
        <div
          className={compact ? 'relative' : 'relative rounded-2xl p-2'}
          style={compact ? undefined : { backgroundColor: '#fafafa' }}
        >
          {toast && (
            <div className="fixed top-4 right-4 z-[70] pointer-events-none">
              <div className="pointer-events-auto">
                <Toast message={toast.msg} type="info" duration={2200} onClose={() => setToast(null)} />
              </div>
            </div>
          )}
          <div
            ref={gridRef}
            className="grid relative"
            style={{
              gridTemplateColumns: templateCols,
              gridTemplateRows: templateRows,
              columnGap: 0,
              rowGap: 0,
            }}
          >
            {Array.from({ length: visRows }).map((_, r) => (
              Array.from({ length: visCols }).map((_, c) => {
                const ownerIdx = findOwner(r, c);
                if (ownerIdx !== -1) {
                  const o = merges[ownerIdx];
                  const isTopLeft = o.r === r && o.c === c;
                  if (!isTopLeft) return null;
                }
                const spanR = ownerIdx !== -1 ? merges[ownerIdx].rs : 1;
                const spanC = ownerIdx !== -1 ? merges[ownerIdx].cs : 1;
                // Compute per-cell effective corner radius with precedence by recency (last change wins)
                const cellRad = (() => {
                  type C = { ts: number; val: number };
                  const candidates: C[] = [];
                  const key = `${r}:${c}`;
                  // Cell override candidate
                  if (isCellEdgeRadiusEnabled(r, c)) {
                    const v = getCellEdgeRadius(r, c);
                    if (typeof v === 'number') candidates.push({ ts: cellEdgeRadiusTsByCell[key] ?? 0, val: Math.max(0, v) });
                  }
                  // Row candidates across the span
                  for (let rr = r; rr < r + spanR && rr < rowHeights.length; rr++) {
                    const v = rowRadiusByIndex[rr];
                    if (typeof v === 'number') candidates.push({ ts: rowRadiusTsByIndex[rr] ?? 0, val: Math.max(0, v) });
                  }
                  // Column candidates across the span
                  for (let cc = c; cc < c + spanC && cc < colFracs.length; cc++) {
                    const v = colRadiusByIndex[cc];
                    if (typeof v === 'number') candidates.push({ ts: colRadiusTsByIndex[cc] ?? 0, val: Math.max(0, v) });
                  }
                  if (candidates.length === 0) return Math.max(0, rad);
                  const latest = candidates.reduce((a,b)=> (b.ts >= a.ts ? b : a));
                  return Math.max(0, latest.val);
                })();
                return (
                <div
                  key={`base-${r}-${c}`}
                  style={{
                    gridColumn: `${(c*2)+1} / span ${(spanC*2)-1}`,
                    gridRow: `${(r*2)+1} / span ${(spanR*2)-1}`,
                    borderRadius: cellRad
                  }}
                  className={"relative border border-gray-200 bg-white/70 shadow-sm min-h-[64px] transition-all hover:shadow-md hover:border-purple-300"}
                  onDragOver={e => editable && e.preventDefault()}
                  onDrop={e => {
                    if (!editable) return;
                    e.preventDefault();
                    const data = e.dataTransfer.getData('text/plain');
                    if (!data) return;
                    const [kind, val] = data.split(':');
                    const x = c + 1; const y = r + 1;
                    if (kind === 'new' && onDropNew) {
                      const t = (val as PageBlock['type']);
                      onDropNew(t, x, y);
                    } else if (kind === 'move' && onMoveBlock) {
                      onMoveBlock(val, x, y);
                    }
                  }}
                  onMouseEnter={() => setHoverCell({ r, c })}
                  onMouseLeave={() => setHoverCell(h => (h && h.r === r && h.c === c) ? null : h)}
                  onClick={(e) => {
                    if ((ctrlDown || shiftDown) && spanR === 1 && spanC === 1) {
                      e.preventDefault();
                      e.stopPropagation();
                      setHoverCell({ r, c });
                    }
                  }}
                >
                  {(() => { const padVal = getPad(r, c); const keep = getKeepRadius(r,c); const width = (colTrackWidths.slice(c, c + spanC).reduce((a,b)=>a+b,0))||0; const height = (rowHeights.slice(r, r + spanR).reduce((a,b)=>a+b,0))||0; const maxPad = Math.max(0, Math.floor(Math.min(width, height) / 2) - 2); const pv = Math.min(padVal, maxPad); const rMax = Math.max(0, Math.floor(Math.min(width, height) / 2) - pv); const customOn = isCustomRadiusEnabled(r,c); const customVal = getCustomRadius(r,c); const baseR = cellRad; const innerR = customOn ? Math.max(0, Math.min(rMax, (customVal ?? baseR))) : (keep ? Math.max(0, Math.min(baseR, rMax)) : Math.max(0, baseR - pv)); return (
                  <>
                  {editable && hoverCell && hoverCell.r === r && hoverCell.c === c && (
                    <div className="absolute inset-0 pointer-events-none" style={{ borderRadius: cellRad, backgroundColor: 'rgba(59,130,246,0.18)' }} />
                  )}
                  <div
                    className="absolute bg-white pointer-events-none"
                    style={{
                      left: pv,
                      top: pv,
                      right: pv,
                      bottom: pv,
                      borderRadius: innerR,
                      border: `${(editable && hoverCell && hoverCell.r === r && hoverCell.c === c) ? 3 : 1}px solid ${
                        (editable && hoverCell && hoverCell.r === r && hoverCell.c === c)
                          ? 'rgba(59,130,246,0.9)'
                          : ((editable || compact) ? '#D1D5DB' : 'transparent')
                      }`
                    }}
                  />
                  {(() => {
                    // Render per-cell component content (image or list)
                    const cellX = c + 1; const cellY = r + 1;
                    const img = page.blocks.find(b => b.type === 'image' && b.x === cellX && b.y === cellY && b.w === 1 && b.h === 1);
                    const listB = page.blocks.find(b => (b.type === 'listV' || b.type === 'listH') && b.x === cellX && b.y === cellY && b.w === 1 && b.h === 1);
                    if (!img && !listB) return null;
                    if (listB) {
                      const fullW = (colTrackWidths.slice(c, c + spanC).reduce((a,b)=>a+b,0)) || 0;
                      const fullH = (rowHeights.slice(r, r + spanR).reduce((a,b)=>a+b,0)) || 0;
                      const boxW = Math.max(0, fullW - pv*2);
                      const boxH = Math.max(0, fullH - pv*2);
                      return (
                        <div className="absolute" style={{ left: pv, top: pv, right: pv, bottom: pv, borderRadius: innerR, overflow: 'hidden', zIndex: 15 }}>
                          <div className="absolute inset-0">
                            <ListCel
                              items={(listB as any).listItems || []}
                              entries={(listB as any).listEntries || undefined}
                              orientation={listB.type === 'listH' ? 'horizontal' : 'vertical'}
                              imageSize={(listB as any).listImageSize}
                              imageRadius={(listB as any).listImageRadius}
                              borderRadius={innerR}
                            />
                          </div>
                          {editable && (
                            <button
                              className="absolute left-1 top-1 h-6 w-6 rounded-full bg-white/70 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center transition opacity-50 hover:opacity-100 z-40"
                              title="Remove from this cell"
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); removeCellBlock(listB.id); }}
                            >
                              <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                            </button>
                          )}
                        </div>
                      );
                    }
                    if (!img) return null;
                    const raw = tempImgOffsets[img.id] || { x: (img as any).imgOffsetX || 0, y: (img as any).imgOffsetY || 0 };
                    const fullW = (colTrackWidths.slice(c, c + spanC).reduce((a,b)=>a+b,0)) || 0;
                    const fullH = (rowHeights.slice(r, r + spanR).reduce((a,b)=>a+b,0)) || 0;
                    const boxW = Math.max(0, fullW - pv*2);
                    const boxH = Math.max(0, fullH - pv*2);
                    const nat = imgNaturalSizes[img.id];
                    // Compute rendered cover size from natural size (no arbitrary scale)
                    let rendW = boxW, rendH = boxH;
                    if (nat && nat.w > 0 && nat.h > 0 && boxW > 0 && boxH > 0) {
                      const baseS = Math.max(boxW / nat.w, boxH / nat.h);
                      const userScale = (adjustingImage && adjustingImage.id === img.id)
                        ? (tempImgScale[img.id] ?? ((img as any).imgScale ?? 1))
                        : (((img as any).imgScale) ?? 1);
                      const s = baseS * Math.max(1, Math.min(8, userScale));
                      rendW = nat.w * s; rendH = nat.h * s;
                    }
                    const eps = 3; // allow tiny extra to ensure we can reach visual edges
                    const maxXNow = Math.max(0, (rendW - boxW) / 2) + eps;
                    const maxYNow = Math.max(0, (rendH - boxH) / 2) + eps;
                    const cur = { x: Math.min(maxXNow, Math.max(-maxXNow, raw.x)), y: Math.min(maxYNow, Math.max(-maxYNow, raw.y)) };
                    return (
                      <div className="absolute" style={{ left: pv, top: pv, right: pv, bottom: pv, borderRadius: innerR, overflow: 'hidden', zIndex: 15 }}>
                        <div
                          className="absolute inset-0"
                          style={{ cursor: (adjustingImage && adjustingImage.id === img.id) ? 'move' : 'default' }}
                          onMouseDown={(e)=> {
                            const fullW = (colTrackWidths.slice(c, c + spanC).reduce((a,b)=>a+b,0)) || 0;
                            const fullH = (rowHeights.slice(r, r + spanR).reduce((a,b)=>a+b,0)) || 0;
                            const boxW = Math.max(0, fullW - pv*2);
                            const boxH = Math.max(0, fullH - pv*2);
                            const nat = imgNaturalSizes[img.id];
                            let rendW = boxW, rendH = boxH;
                            if (nat && nat.w > 0 && nat.h > 0 && boxW > 0 && boxH > 0) {
                              const baseS = Math.max(boxW / nat.w, boxH / nat.h);
                              const userScale = (adjustingImage && adjustingImage.id === img.id)
                                ? (tempImgScale[img.id] ?? ((img as any).imgScale ?? 1))
                                : (((img as any).imgScale) ?? 1);
                              const s = baseS * Math.max(1, Math.min(8, userScale));
                              rendW = nat.w * s; rendH = nat.h * s;
                            }
                            onImgPanDown(e, img.id, cur.x, cur.y, boxW, boxH, rendW, rendH);
                          }}
                        >
                          <ImageCel
                            url={img.url}
                            borderRadius={innerR}
                            offsetX={cur.x}
                            offsetY={cur.y}
                            widthPx={nat ? rendW : undefined}
                            heightPx={nat ? rendH : undefined}
                            naturalW={nat ? nat.w : undefined}
                            naturalH={nat ? nat.h : undefined}
                            onLoadNatural={(w,h)=> setImgNaturalSizes(prev => (prev[img.id] && prev[img.id].w === w && prev[img.id].h === h) ? prev : { ...prev, [img.id]: { w, h } })}
                            adjusting={!!(adjustingImage && adjustingImage.id === img.id)}
                          />
                        </div>
                        {editable && (
                          <button
                            className="absolute left-1 top-1 h-6 w-6 rounded-full bg-white/70 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center transition opacity-50 hover:opacity-100 z-40"
                            title="Remove from this cell"
                            onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); removeCellBlock(img.id); }}
                          >
                            <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                          </button>
                        )}
                        {(adjustingImage && adjustingImage.id === img.id) && (
                          <div className="absolute left-2 top-2 z-20 flex items-center gap-2">
                            <button
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-green-50"
                              title="Confirm position"
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); const cur2 = tempImgOffsets[img.id] || { x: 0, y: 0 }; const scaleNow = tempImgScale[img.id] ?? ((img as any).imgScale ?? 1); if (onUpdateBlock) onUpdateBlock(img.id, { imgOffsetX: cur2.x, imgOffsetY: cur2.y, imgScale: Math.max(1, Math.min(8, scaleNow)) }); setAdjustingImage(null); }}
                            >
                              <svg className="h-4 w-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                            </button>
                            <button
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-gray-50"
                              title="Zoom out"
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setTempImgScale(prev => { const curS = prev[img.id] ?? ((img as any).imgScale ?? 1); const next = Math.max(1, curS * 0.9); return { ...prev, [img.id]: next }; }); }}
                            >
                              <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/></svg>
                            </button>
                            <button
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-gray-50"
                              title="Zoom in"
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setTempImgScale(prev => { const curS = prev[img.id] ?? ((img as any).imgScale ?? 1); const next = Math.min(8, curS * 1.1); return { ...prev, [img.id]: next }; }); }}
                            >
                              <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                            </button>
                            <button
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-red-50"
                              title="Cancel"
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setTempImgOffsets(prev => { const { [img.id]: _, ...rest } = prev; return rest; }); setAdjustingImage(null); }}
                            >
                              <svg className="h-4 w-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(hoverCell && hoverCell.r === r && hoverCell.c === c && editable) && (
                    <>
                      <div
                        className="absolute z-40"
                        style={{ left: pv - 4, top: pv, bottom: pv, width: 8, cursor: 'col-resize' }}
                        onMouseDown={(e)=>onPadDown(e,'left',r,c,spanR,spanC)}
                        title={`Padding: ${pv}px (max ${maxPad}px)`}
                      />
                      <div
                        className="absolute z-40"
                        style={{ right: pv - 4, top: pv, bottom: pv, width: 8, cursor: 'col-resize' }}
                        onMouseDown={(e)=>onPadDown(e,'right',r,c,spanR,spanC)}
                        title={`Padding: ${pv}px (max ${maxPad}px)`}
                      />
                      <div
                        className="absolute z-40"
                        style={{ left: pv, right: pv, top: pv - 4, height: 8, cursor: 'row-resize' }}
                        onMouseDown={(e)=>onPadDown(e,'top',r,c,spanR,spanC)}
                        title={`Padding: ${pv}px (max ${maxPad}px)`}
                      />
                      <div
                        className="absolute z-40"
                        style={{ left: pv, right: pv, bottom: pv - 4, height: 8, cursor: 'row-resize' }}
                        onMouseDown={(e)=>onPadDown(e,'bottom',r,c,spanR,spanC)}
                        title={`Padding: ${pv}px (max ${maxPad}px)`}
                      />
                    </>
                  )}
                  {editable && (
                  <button
                    className="absolute right-1 top-1 h-6 w-6 rounded-full bg-white/70 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center transition opacity-50 hover:opacity-100 z-40"
                    onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); settingsAnchorRef.current = e.currentTarget as HTMLElement; const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect(); const panelW = 224; const gap = 8; const top = rect.bottom + gap; const leftRaw = rect.right - panelW; const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw)); setSettingsFor(s => (s && s.r===r && s.c===c) ? null : { r, c, pos: { top, left } }); }}
                    title="Settings"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0A1.65 1.65 0 0 0 9 3.09V3a2 2 0 1 1 4 0v.09c0 .67.39 1.27 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06c-.46.46-.6 1.14-.33 1.73.14.32.33.62.57.87.24.24.54.43.86.57.59.27 1.27.13 1.73-.33l.06-.06"/></svg>
                  </button>
                  )}
                  {editable && (
                  <button
                    className="absolute right-1 top-9 h-6 w-6 rounded-full bg-gradient-to-br from-white/90 to-white/70 hover:from-white hover:to-white border border-blue-200 shadow-sm flex items-center justify-center text-blue-600 hover:text-blue-700 transition opacity-80 hover:opacity-100 z-40"
                    onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); quickAddAnchorRef.current = e.currentTarget as HTMLElement; const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect(); const panelW = 256; const gap = 8; const top = rect.bottom + gap; const leftRaw = rect.right - panelW; const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw)); setQuickAddFor(s => (s && s.r===r && s.c===c) ? null : { r, c, pos: { top, left } }); }}
                    title="Quick add"
                    aria-label="Quick add"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                  </button>
                  )}
                  {editable && (
                  <button
                    className="absolute right-1 top-16 h-6 w-6 rounded-full bg-gradient-to-br from-white/90 to-white/70 hover:from-white hover:to-white border border-purple-200 shadow-sm flex items-center justify-center text-purple-600 hover:text-purple-700 transition opacity-80 hover:opacity-100 z-40"
                    onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); listAnchorRef.current = e.currentTarget as HTMLElement; const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect(); const panelW = 256; const gap = 8; const top = rect.bottom + gap; const leftRaw = rect.right - panelW; const left = Math.max(8, Math.min(window.innerWidth - panelW - 8, leftRaw)); setListFor(s => (s && s.r===r && s.c===c) ? null : { r, c, pos: { top, left } }); }}
                    title="List elements"
                    aria-label="List elements"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="3" cy="6" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="18" r="1"/></svg>
                  </button>
                  )}
                  {(quickAddFor && quickAddFor.r===r && quickAddFor.c===c) && (
                    createPortal(
                      <QuickAddPopup
                        top={quickAddFor.pos?.top ?? 80}
                        left={quickAddFor.pos?.left ?? 80}
                        onClose={()=> setQuickAddFor(null)}
                        onSelect={(key)=> { setQuickAddFor(null); if (key==='heading') { const cellKey = `${r}:${c}`; const item: HeadingCfg = { id: newOverlayId(), text: 'Heading', align: 'left', color: 'text-gray-900', shadow: true, x: 12, y: 12 }; setHeadingByCell(prev => { const raw = (prev as any)[cellKey]; const arr: HeadingCfg[] = Array.isArray(raw) ? raw : (raw ? [raw] : []); const out = { ...(prev as any), [cellKey]: [...arr, item] } as any; emitCellStyles({ headingByCell: out as any }); return out; }); showToast('Heading added'); } else if (key==='button') { const cellKey = `${r}:${c}`; const item: ButtonCfg = { id: newOverlayId(), label: 'Click me', textColor: '#ffffff', bgMode: 'gradient', gradFrom: '#2563eb', gradTo: '#4f46e5', radius: 18, shadow: true, height: 36, paddingX: 16, fontSize: 14, glow: false, outlineColor: '#ffffff', outlineWidth: 0, capsule: false, outlineMode: 'color', outlineGradFrom: '#2563eb', outlineGradTo: '#4f46e5', x: 12, y: 48 }; setButtonByCell(prev => { const raw = (prev as any)[cellKey]; const arr: ButtonCfg[] = Array.isArray(raw) ? raw : (raw ? [raw] : []); const out = { ...(prev as any), [cellKey]: [...arr, item] } as any; emitCellStyles({ buttonByCell: out as any }); return out; }); showToast('Button added'); } else { showToast(`${key} coming soon`); } }}
                      />,
                      document.body
                    )
                  )}
                  {(listFor && listFor.r===r && listFor.c===c) && (
                    createPortal(
                      <CellOverlayListPopup2
                        top={listFor.pos?.top ?? 80}
                        left={listFor.pos?.left ?? 80}
                        items={(() => {
                          const cellKey = `${r}:${c}`;
                          const rawH = (headingByCell as any)[cellKey];
                          const hs: HeadingCfg[] = Array.isArray(rawH) ? rawH : (rawH ? [rawH] : []);
                          const rawB = (buttonByCell as any)[cellKey];
                          const bs: ButtonCfg[] = Array.isArray(rawB) ? rawB : (rawB ? [rawB] : []);
                          const mappedH: OverlayListItem[] = hs.map(h=> ({ id: h.id, kind: 'heading', title: h.text || 'Heading' }));
                          const mappedB: OverlayListItem[] = bs.map(b=> ({ id: b.id, kind: 'button', title: b.label || 'Button' } as any));
                          return [...mappedH, ...mappedB];
                        })()}
                        onClose={()=> setListFor(null)}
                        onSettings={(id, kind, anchor)=>{ overlaySettingsAnchorRef.current = anchor; setOverlaySettingsFor({ r, c, id, kind, pos: listFor?.pos }); }}
                        onDelete={(id, kind)=>{
                          const key = `${r}:${c}`;
                          if (kind==='heading') {
                            setHeadingByCell(prev=>{ const arr: HeadingCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const next = arr.filter(o=> o.id!==id); const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ headingByCell: out as any }); return out; });
                          } else {
                            setButtonByCell(prev=>{ const arr: ButtonCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const next = arr.filter(o=> o.id!==id); const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ buttonByCell: out as any }); return out; });
                          }
                        }}
                        onDuplicate={(id, kind)=>{
                          const key = `${r}:${c}`;
                          if (kind==='heading') {
                            setHeadingByCell(prev=>{ const arr: HeadingCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const found = arr.find(o=> o.id===id); if (!found) return prev; const copy: HeadingCfg = { ...found, id: newOverlayId(), x: (found.x||0)+12, y: (found.y||0)+12, text: found.text ? `${found.text}` : 'Heading' }; const out = { ...(prev as any), [key]: [...arr, copy] } as any; emitCellStyles({ headingByCell: out as any }); return out; });
                          } else {
                            setButtonByCell(prev=>{ const arr: ButtonCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const found = arr.find(o=> o.id===id); if (!found) return prev; const copy: ButtonCfg = { ...found, id: newOverlayId(), x: (found.x||0)+12, y: (found.y||0)+12, label: found.label ? `${found.label}` : 'Button' }; const out = { ...(prev as any), [key]: [...arr, copy] } as any; emitCellStyles({ buttonByCell: out as any }); return out; });
                          }
                        }}
                        onMoveUp={(id, kind)=>{
                          const key = `${r}:${c}`;
                          if (kind==='heading') {
                            setHeadingByCell(prev=>{ const arr: HeadingCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const idx = arr.findIndex(o=> o.id===id); if (idx<=0) return prev; const next = arr.slice(); const tmp = next[idx-1]; next[idx-1] = next[idx]; next[idx] = tmp; const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ headingByCell: out as any }); return out; });
                          } else {
                            setButtonByCell(prev=>{ const arr: ButtonCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const idx = arr.findIndex(o=> o.id===id); if (idx<=0) return prev; const next = arr.slice(); const tmp = next[idx-1]; next[idx-1] = next[idx]; next[idx] = tmp; const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ buttonByCell: out as any }); return out; });
                          }
                        }}
                        onMoveDown={(id, kind)=>{
                          const key = `${r}:${c}`;
                          if (kind==='heading') {
                            setHeadingByCell(prev=>{ const arr: HeadingCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const idx = arr.findIndex(o=> o.id===id); if (idx<0 || idx>=arr.length-1) return prev; const next = arr.slice(); const tmp = next[idx+1]; next[idx+1] = next[idx]; next[idx] = tmp; const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ headingByCell: out as any }); return out; });
                          } else {
                            setButtonByCell(prev=>{ const arr: ButtonCfg[] = Array.isArray((prev as any)[key]) ? (prev as any)[key] : ((prev as any)[key] ? [(prev as any)[key]] : []); const idx = arr.findIndex(o=> o.id===id); if (idx<0 || idx>=arr.length-1) return prev; const next = arr.slice(); const tmp = next[idx+1]; next[idx+1] = next[idx]; next[idx] = tmp; const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ buttonByCell: out as any }); return out; });
                          }
                        }}
                      />,
                      document.body
                    )
                  )}
                  {(overlaySettingsFor && overlaySettingsFor.r===r && overlaySettingsFor.c===c) && (
                    createPortal(
                      <CellOverlaySettingsPopup
                        top={overlaySettingsFor.pos?.top ?? 80}
                        left={overlaySettingsFor.pos?.left ?? 80}
                        kind={overlaySettingsFor.kind}
                        panelRef={overlaySettingsPanelRef}
                        value={(()=>{
                          const key = `${r}:${c}`;
                          if (overlaySettingsFor.kind==='heading') {
                            const arr = (headingByCell as any)[key] as HeadingCfg[]|undefined; const found = (arr||[]).find(o=>o.id===overlaySettingsFor.id);
                            return { text: found?.text, color: found?.color, fontFamily: (found as any)?.fontFamily } as HeadingSettings;
                          } else {
                            const arr = (buttonByCell as any)[key] as ButtonCfg[]|undefined; const found = (arr||[]).find(o=>o.id===overlaySettingsFor.id);
                            return {
                              label: found?.label,
                              textColor: found?.textColor,
                              bgMode: found?.bgMode ?? 'solid',
                              bgColor: found?.bgColor ?? '#2563eb',
                              gradFrom: found?.gradFrom ?? '#2563eb',
                              gradTo: found?.gradTo ?? '#4f46e5',
                              radius: found?.radius ?? 10,
                              shadow: (found?.shadow ?? true),
                              height: found?.height ?? 36,
                              paddingX: found?.paddingX ?? 16,
                              fontSize: found?.fontSize ?? 14,
                              glow: (found?.glow ?? false),
                              outlineColor: found?.outlineColor,
                              outlineWidth: found?.outlineWidth ?? 0,
                              capsule: found?.capsule ?? false,
                              outlineMode: (found?.outlineMode ?? 'color') as any,
                              outlineGradFrom: found?.outlineGradFrom ?? '#2563eb',
                              outlineGradTo: found?.outlineGradTo ?? '#4f46e5',
                              fontFamily: (found as any)?.fontFamily
                            } as ButtonSettings;
                          }
                        })()}
                        onChange={(v)=>{
                          const key = `${r}:${c}`;
                          if (overlaySettingsFor.kind==='heading') {
                            setHeadingByCell(prev=>{ const raw=(prev as any)[key]; const arr: HeadingCfg[] = Array.isArray(raw)?raw:(raw?[raw]:[]); const hv = v as any; const next = arr.map(o=> o.id===overlaySettingsFor.id ? { ...o, text: hv.text, color: hv.color, fontFamily: hv.fontFamily } : o); const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ headingByCell: out as any }); return out; });
                          } else {
                            setButtonByCell(prev=>{ const raw=(prev as any)[key]; const arr: ButtonCfg[] = Array.isArray(raw)?raw:(raw?[raw]:[]); const bv = (v as any) as ButtonCfg; const next = arr.map(o=> o.id===overlaySettingsFor.id ? { ...o, label: bv.label, textColor: bv.textColor, bgMode: bv.bgMode, bgColor: bv.bgColor, gradFrom: bv.gradFrom, gradTo: bv.gradTo, radius: bv.radius, shadow: bv.shadow, height: bv.height, paddingX: bv.paddingX, fontSize: bv.fontSize, glow: bv.glow, outlineColor: bv.outlineColor, outlineWidth: bv.outlineWidth, capsule: bv.capsule, outlineMode: bv.outlineMode, outlineGradFrom: bv.outlineGradFrom, outlineGradTo: bv.outlineGradTo, fontFamily: (bv as any).fontFamily } : o); const out = { ...(prev as any), [key]: next } as any; emitCellStyles({ buttonByCell: out as any }); return out; });
                          }
                        }}
                        onClose={()=> setOverlaySettingsFor(null)}
                      />,
                      document.body
                    )
                  )}
                  {/* Render overlays for this cell */}
                  {(() => {
                    const cellKey = `${r}:${c}`;
                    const rawH = (headingByCell as any)[cellKey];
                    const itemsH: HeadingCfg[] = Array.isArray(rawH) ? rawH : (rawH ? [rawH] : []);
                    const rawB = (buttonByCell as any)[cellKey];
                    const itemsB: ButtonCfg[] = Array.isArray(rawB) ? rawB : (rawB ? [rawB] : []);
                    if ((!itemsH || !itemsH.length) && (!itemsB || !itemsB.length)) return null;
                    return (
                      <div className="absolute inset-0 z-30 p-3">
                        {itemsH.map(hCfg => {
                          const left = Math.max(0, (hCfg.x ?? 12));
                          const top = Math.max(0, (hCfg.y ?? 12));
                          return (
                            <div
                              key={hCfg.id}
                              className={`absolute ${editable ? 'cursor-move' : ''}`}
                              style={{ left: 0, top: 0, transform: `translate3d(${left}px, ${top}px, 0)`, willChange: 'transform' }}
                              onMouseDown={(e)=> onHeadingDown(e, cellKey, hCfg.id)}
                            >
                              <div className={`${editable ? 'pointer-events-none ring-0' : ''}`}>
                                <HeadingOverlay text={hCfg.text} align={hCfg.align} color={hCfg.color} shadow={hCfg.shadow} fontFamily={(hCfg as any).fontFamily} />
                              </div>
                            </div>
                          );
                        })}
                        {itemsB.map(bCfg => {
                          const left = Math.max(0, (bCfg.x ?? 12));
                          const top = Math.max(0, (bCfg.y ?? 12));
                          return (
                            <div
                              key={bCfg.id}
                              className={`absolute ${editable ? 'cursor-move' : ''}`}
                              style={{ left: 0, top: 0, transform: `translate3d(${left}px, ${top}px, 0)`, willChange: 'transform' }}
                              onMouseDown={(e)=> onButtonDown(e, cellKey, bCfg.id)}
                            >
                              <div className={`${editable ? 'pointer-events-none ring-0' : ''}`}>
                                <ButtonOverlay label={bCfg.label} textColor={bCfg.textColor} bgMode={bCfg.bgMode} bgColor={bCfg.bgColor} gradFrom={bCfg.gradFrom} gradTo={bCfg.gradTo} radius={bCfg.radius} shadow={bCfg.shadow} height={bCfg.height} paddingX={bCfg.paddingX} fontSize={bCfg.fontSize} glow={bCfg.glow} outlineColor={bCfg.outlineColor} outlineWidth={bCfg.outlineWidth} capsule={bCfg.capsule} outlineMode={bCfg.outlineMode} outlineGradFrom={bCfg.outlineGradFrom} outlineGradTo={bCfg.outlineGradTo} fontFamily={(bCfg as any).fontFamily} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {(() => {
                    // Center '+' add-content button for empty cells (respecting merge spans)
                    const cellLeft = c + 1; const cellTop = r + 1;
                    const rs = spanR; const cs = spanC;
                    const rect = { l: cellLeft, r: cellLeft + cs, t: cellTop, b: cellTop + rs };
                    const hasBlock = page.blocks.some(b => !(b.x + b.w <= rect.l || rect.r <= b.x || b.y + b.h <= rect.t || rect.b <= b.y));
                    if (!editable || hasBlock) return null;
                    return (
                      <button
                        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white border border-blue-200 shadow flex items-center justify-center text-blue-600 hover:text-blue-700 transition z-40"
                        title="Add content"
                        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); showToast('Select a block from the palette, then drop it into this cell'); }}
                      >
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                      </button>
                    );
                  })()}
                  {(settingsFor && settingsFor.r===r && settingsFor.c===c) && (
                    createPortal(
                    <div ref={el => { settingsPanelRef.current = el; }} className="fixed z-[2100] bg-white rounded-xl border border-gray-200 shadow-lg p-3 w-56"
                      style={{ top: settingsFor.pos?.top ?? 80, left: settingsFor.pos?.left ?? 80 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-900">Cell settings</div>
                        <button className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-gray-50" onClick={()=>setSettingsFor(null)} title="Close">
                          <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                        </button>
                      </div>
                      {(() => { const p = getPad(r,c); const keep = getKeepRadius(r,c); const customOn = isCustomRadiusEnabled(r,c); const customVal = getCustomRadius(r,c); const width = (colTrackWidths.slice(c, c + spanC).reduce((a,b)=>a+b,0))||0; const height = (rowHeights.slice(r, r + spanR).reduce((a,b)=>a+b,0))||0; const maxPad = Math.max(0, Math.floor(Math.min(width, height) / 2) - 2); const rMax = Math.max(0, Math.floor(Math.min(width, height) / 2) - Math.min(p, maxPad)); const shownCustom = Math.min(rMax, (customVal ?? rad)); const cellX = c+1; const cellY = r+1; const imgHere = page.blocks.find(b => b.type==='image' && b.x===cellX && b.y===cellY && b.w===1 && b.h===1); const listHere = page.blocks.find(b => (b.type==='listV' || b.type==='listH') && b.x===cellX && b.y===cellY && b.w===1 && b.h===1); const key = `${r}:${c}`; const edgeOn = isCellEdgeRadiusEnabled(r,c); const edgeVal = getCellEdgeRadius(r,c); const latestEffective = (()=>{ type C={ts:number;val:number}; const cs:C[]=[]; if(edgeOn && typeof edgeVal==='number') cs.push({ ts: cellEdgeRadiusTsByCell[key] ?? 0, val: Math.max(0,edgeVal) }); for(let rr=r; rr<r+spanR && rr<rowHeights.length; rr++){ const v=rowRadiusByIndex[rr]; if(typeof v==='number') cs.push({ ts: rowRadiusTsByIndex[rr] ?? 0, val: Math.max(0,v) }); } for(let cc=c; cc<c+spanC && cc<colFracs.length; cc++){ const v=colRadiusByIndex[cc]; if(typeof v==='number') cs.push({ ts: colRadiusTsByIndex[cc] ?? 0, val: Math.max(0,v) }); } if(cs.length===0) return Math.max(0,rad); const latest=cs.reduce((a,b)=> (b.ts>=a.ts?b:a)); return latest.val; })(); return (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm text-gray-700"><span>Edge radius</span><span className="font-medium text-gray-900">{Math.round(latestEffective)}px</span></div>
                          <input type="range" min={0} max={32} value={Math.max(0, Math.min(32, Math.round(latestEffective)))} onChange={(e)=>{ const v = Math.max(0, Math.min(32, parseInt(e.target.value,10)||0)); const ts = Date.now(); setCellEdgeRadiusEnabledByCell(prev => { const out = { ...prev, [key]: true }; emitCellStyles({ customRadiusEnabledByCell: customRadiusEnabledByCell, cellEdgeRadiusByCell }); return out; }); setCellEdgeRadiusByCell(prev => { const out = { ...prev, [key]: v }; emitCellStyles({ cellEdgeRadiusByCell: out }); return out; }); setCellEdgeRadiusTsByCell(prev => ({ ...prev, [key]: ts })); }} className="w-full accent-indigo-600" />
                          <div className="flex items-center justify-between">
                            <button className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 text-sm" onClick={()=>{ setCellEdgeRadiusEnabledByCell(prev=>{ const { [key]:_, ...rest } = prev; emitCellStyles({ customRadiusEnabledByCell: customRadiusEnabledByCell, cellEdgeRadiusByCell }); return rest; }); setCellEdgeRadiusByCell(prev=>{ const { [key]:_, ...rest } = prev; emitCellStyles({ cellEdgeRadiusByCell: rest }); return rest; }); setCellEdgeRadiusTsByCell(prev=>{ const { [key]:_, ...rest } = prev; return rest; }); }}>
                              Reset edge
                            </button>
                          </div>
                          <div className="h-px bg-gray-100 my-2" />
                          <div className="flex items-center justify-between text-sm text-gray-700"><span>Padding</span><span className="font-medium text-gray-900">{p}px</span></div>
                          <input type="range" min={0} max={maxPad} value={Math.min(p, maxPad)} onChange={(e)=>{ const raw = parseInt(e.target.value,10)||0; const v = Math.max(0, Math.min(maxPad, raw)); const key = `${r}:${c}`; setPadByCell(prev=>{ const out = { ...prev, [key]: v }; emitCellStyles({ padByCell: out }); return out; }); }} className="w-full accent-blue-600" />
                          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                            <input type="checkbox" checked={keep} disabled={customOn} onChange={(e)=>{ const key = `${r}:${c}`; const checked = e.target.checked; setKeepRadiusByCell(prev => { const out = { ...prev, [key]: checked }; emitCellStyles({ keepRadiusByCell: out }); return out; }); }} />
                            <span>Preserve corner radius</span>
                          </label>
                          <div className="text-[11px] text-gray-500">Max inner radius now ~ {rMax}px</div>
                          <div className="h-px bg-gray-100 my-2" />
                          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                            <input type="checkbox" checked={customOn} onChange={(e)=>{ const key = `${r}:${c}`; const on = e.target.checked; setCustomRadiusEnabledByCell(prev => { const out = { ...prev, [key]: on }; emitCellStyles({ customRadiusEnabledByCell: out }); return out; }); if (on && customRadiusByCell[key] == null) { const init = Math.max(0, Math.min(rMax, rad)); setCustomRadiusByCell(prev => { const out = { ...prev, [key]: init }; emitCellStyles({ customRadiusByCell: out }); return out; }); } }} />
                            <span>Use custom corner radius</span>
                          </label>
                          {customOn && (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-sm text-gray-700"><span>Corner radius</span><span className="font-medium text-gray-900">{Math.round(shownCustom)}px</span></div>
                              <input type="range" min={0} max={rMax} value={Math.min(shownCustom, rMax)} onChange={(e)=>{ const raw = parseInt(e.target.value,10)||0; const v = Math.max(0, Math.min(rMax, raw)); const key = `${r}:${c}`; setCustomRadiusByCell(prev => { const out = { ...prev, [key]: v }; emitCellStyles({ customRadiusByCell: out }); return out; }); }} className="w-full accent-purple-600" />
                            </div>
                          )}
                          {imgHere && (
                            <>
                              <div className="h-px bg-gray-100 my-2" />
                              <div className="text-sm font-medium text-gray-900">Image</div>
                              <div className="text-xs text-gray-500 mb-1">Manage the image for this cell</div>
                              <button
                                className="inline-flex items-center justify-center w-full rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-2 text-sm font-medium shadow hover:from-blue-700 hover:to-indigo-700"
                                onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setMediaPickerFor({ id: imgHere.id }); }}
                              >
                                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 15l4-4 4 4 5-5 4 4"/></svg>
                                Choose Image
                              </button>
                              <button
                                className="mt-2 inline-flex items-center justify-center w-full rounded-lg border border-blue-200 bg-white text-blue-700 px-3 py-2 text-sm font-medium shadow-sm hover:bg-blue-50"
                                onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setAdjustingImage({ id: imgHere.id }); setSettingsFor(null); const baseX = (imgHere as any).imgOffsetX || 0; const baseY = (imgHere as any).imgOffsetY || 0; const baseS = (imgHere as any).imgScale ?? 1; setTempImgOffsets(prev => ({ ...prev, [imgHere.id]: { x: baseX, y: baseY } })); setTempImgScale(prev => ({ ...prev, [imgHere.id]: Math.max(1, Math.min(8, baseS)) })); }}
                              >
                                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 8h16"/><path d="M4 12h16"/><path d="M4 16h16"/></svg>
                                Adjust Image Position
                              </button>
                            </>
                          )}
                          {listHere && (
                            <>
                              <div className="h-px bg-gray-100 my-2" />
                              <div className="text-sm font-medium text-gray-900">List</div>
                              <div className="text-xs text-gray-500 mb-1">Edit the items for this list</div>
                              <button
                                className="inline-flex items-center justify-center w-full rounded-lg border border-purple-200 bg-white text-purple-700 px-3 py-2 text-sm font-medium shadow-sm hover:bg-purple-50"
                                onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setListEditorFor({ id: listHere.id, orientation: listHere.type==='listH'?'horizontal':'vertical' }); setSettingsFor(null); }}
                              >
                                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="3" cy="6" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="18" r="1"/></svg>
                                Open List Editor
                              </button>
                            </>
                          )}
                          {(spanR > 1 || spanC > 1) && (
                            <>
                              <div className="h-px bg-gray-100 my-2" />
                              <button
                                className="inline-flex items-center justify-center w-full rounded-lg border border-red-200 bg-white text-red-700 px-3 py-2 text-sm font-medium shadow-sm hover:bg-red-50"
                                onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); if (beginHistoryGroup) beginHistoryGroup(); splitMergeAt(r, c); setSettingsFor(null); showToast('Split into single cells'); if (commitHistoryGroup) commitHistoryGroup(); }}
                              >
                                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                                Split into single cells
                              </button>
                            </>
                          )}
                          <div className="flex items-center justify-end pt-2">
                            <button
                              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm hover:from-blue-700 hover:to-indigo-700"
                              onClick={()=>setSettingsFor(null)}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      ); })()}
                    </div>, document.body)
                  )}
                  </>
                  ); })()}
                  {editable && (
                    <>
                      {/* Top controls: centered group */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center gap-1">
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-blue-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                          title="Expand upward"
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); if (beginHistoryGroup) beginHistoryGroup(); expandUp(r, c); if (commitHistoryGroup) commitHistoryGroup(); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5l-5 5M12 5l5 5"/><path d="M12 5v14"/></svg>
                        </button>
                        {(spanR === 1 && spanC === 1 && !rowsBlockedByMerge[r] && !colsBlockedByMerge[c]) && (
                          <button
                            className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-green-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                            title="Duplicate row"
                            onMouseDown={e => { e.stopPropagation(); }}
                            onClick={e => { e.preventDefault(); e.stopPropagation(); /* duplicate row later */ }}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="10" height="10" rx="2"/><rect x="10" y="10" width="10" height="10" rx="2"/></svg>
                          </button>
                        )}
                      </div>

                      {/* Right controls: centered vertical group */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 flex flex-col items-center gap-1">
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-blue-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                          title="Expand right"
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); if (beginHistoryGroup) beginHistoryGroup(); expandRight(r, c); if (commitHistoryGroup) commitHistoryGroup(); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12l-5 5M19 12l-5-5"/><path d="M5 12h14"/></svg>
                        </button>
                        {(spanR === 1 && spanC === 1 && !rowsBlockedByMerge[r] && !colsBlockedByMerge[c]) && (
                          <button
                            className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-green-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                            title="Duplicate column"
                            onMouseDown={e => { e.stopPropagation(); }}
                            onClick={e => { e.preventDefault(); e.stopPropagation(); /* duplicate column later */ }}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="10" height="10" rx="2"/><rect x="10" y="10" width="10" height="10" rx="2"/></svg>
                          </button>
                        )}
                      </div>

                      {/* Bottom controls: centered group */}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-20 flex items-center gap-1">
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-blue-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                          title="Expand down"
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); if (beginHistoryGroup) beginHistoryGroup(); expandDown(r, c); if (commitHistoryGroup) commitHistoryGroup(); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l-5-5M12 19l5-5"/><path d="M12 5v14"/></svg>
                        </button>
                        {(spanR === 1 && spanC === 1 && !rowsBlockedByMerge[r] && !colsBlockedByMerge[c]) && (
                          <button
                            className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-green-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                            title="Duplicate row"
                            onMouseDown={e => { e.stopPropagation(); }}
                            onClick={e => { e.preventDefault(); e.stopPropagation(); /* duplicate row later */ }}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="10" height="10" rx="2"/><rect x="10" y="10" width="10" height="10" rx="2"/></svg>
                          </button>
                        )}
                      </div>

                      {/* Left controls: centered vertical group */}
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1">
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-blue-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                          title="Expand left"
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); if (beginHistoryGroup) beginHistoryGroup(); expandLeft(r, c); if (commitHistoryGroup) commitHistoryGroup(); }}
                        >
                          <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12l5 5M5 12l5-5"/><path d="M5 12h14"/></svg>
                        </button>
                        {(spanR === 1 && spanC === 1 && !rowsBlockedByMerge[r] && !colsBlockedByMerge[c]) && (
                          <button
                            className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow hover:bg-green-50 transform transition duration-150 opacity-[0.34] hover:opacity-100 scale-[0.66] hover:scale-100"
                            title="Duplicate column"
                            onMouseDown={e => { e.stopPropagation(); }}
                            onClick={e => { e.preventDefault(); e.stopPropagation(); /* duplicate column later */ }}
                          >
                            <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-gray-700" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="10" height="10" rx="2"/><rect x="10" y="10" width="10" height="10" rx="2"/></svg>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ); })
            ))}
            {editable && shiftDown && hoverCell && (
              (() => {
                const r = hoverCell.r;
                const c = hoverCell.c;
                const ownerIdx = findOwner(r, c);
                const isSingle = ownerIdx === -1 || (merges[ownerIdx].rs === 1 && merges[ownerIdx].cs === 1);
                if (!isSingle) return null;
                const top = rowHeights.slice(0, r).reduce((a,b)=>a+b,0) + rowGaps.slice(0, r).reduce((a,b)=>a+b,0);
                const height = rowHeights[r] ?? 0;
                return (
                  <div
                    key={`row-overlay-${r}`}
                    className="absolute pointer-events-none z-60 rounded-2xl ring-2 ring-blue-500 ring-offset-2 ring-offset-[#fafafa]"
                    style={{ left: 0, top, width: totalGridWidth, height, borderRadius: rad }}
                  />
                );
              })()
            )}
            {editable && !shiftDown && ctrlDown && hoverCell && (
              (() => {
                const c = hoverCell.c;
                const r = hoverCell.r;
                const ownerIdx = findOwner(r, c);
                const isSingle = ownerIdx === -1 || (merges[ownerIdx].rs === 1 && merges[ownerIdx].cs === 1);
                if (!isSingle) return null;
                const left = colTrackWidths.slice(0, c).reduce((a,b)=>a+b,0) + colGaps.slice(0, c).reduce((a,b)=>a+b,0);
                const width = colTrackWidths[c] ?? 0;
                return (
                  <div
                    key={`col-overlay-${c}`}
                    className="absolute pointer-events-none z-60 rounded-2xl ring-2 ring-blue-500 ring-offset-2 ring-offset-[#fafafa]"
                    style={{ left, top: 0, width, height: totalGridHeight, borderRadius: Math.max(rad, colRadiusByIndex[c] ?? 0) }}
                  />
                );
              })()
            )}
            {page.blocks.map((b) => {
            const effRad = effectiveRadiusForBlock(b);
            const style: React.CSSProperties = {
              gridColumn: `${b.x} / span ${b.w}`,
              gridRow: `${b.y} / span ${b.h}`,
              zIndex: 10,
              borderRadius: effRad,
            };
            switch (b.type) {
              case 'text':
                return (
                  <div key={b.id} className="relative rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                       style={style}
                       draggable={editable}
                       onDragStart={e => { if (!editable) return; e.dataTransfer.setData('text/plain', `move:${b.id}`); }}>
                    {editable && (
                      <button
                        aria-label="Drag block"
                        className="absolute left-1 top-1 h-6 w-6 rounded-full bg-white/80 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-600 cursor-move z-20"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                      </button>
                    )}
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: b.content || '' }} />
                  </div>
                );
              case 'image':
                return null; // images are rendered per-cell via ImageCel
              case 'hero':
                return (
                  <div key={b.id} className="relative rounded-2xl overflow-hidden shadow-md"
                       style={style}
                       draggable={editable}
                       onDragStart={e => { if (!editable) return; e.dataTransfer.setData('text/plain', `move:${b.id}`); }}>
                    {editable && (
                      <button
                        aria-label="Drag block"
                        className="absolute left-1 top-1 h-6 w-6 rounded-full bg-white/80 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-600 cursor-move z-20"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                      </button>
                    )}
                    <div
                      className="absolute inset-0 bg-center bg-cover"
                      style={{ backgroundImage: `url('${b.url || ''}')` }}
                    />
                    <div className="relative p-8 sm:p-12 bg-black/40 h-full w-full flex items-end">
                      <h2 className="text-white text-3xl font-bold drop-shadow">{b.content || 'Hero Title'}</h2>
                    </div>
                  </div>
                );
              case 'cta':
                return (
                  <div key={b.id} className="relative rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm"
                       style={style}
                       draggable={editable}
                       onDragStart={e => { if (!editable) return; e.dataTransfer.setData('text/plain', `move:${b.id}`); }}>
                    {editable && (
                      <button
                        aria-label="Drag block"
                        className="absolute left-1 top-1 h-6 w-6 rounded-full bg-white/80 hover:bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-600 cursor-move z-20"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6h6M9 12h6M9 18h6"/></svg>
                      </button>
                    )}
                    <div className="text-lg font-semibold text-gray-900 mb-2">{b.content || 'Call to action'}</div>
                    <button className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 text-sm hover:from-purple-700 hover:to-blue-700">Get started</button>
                  </div>
                );
              case 'listV':
                return null; // lists are rendered per-cell via ListCel
              case 'listH':
                return null; // lists are rendered per-cell via ListCel
              default:
                return null;
            }
            })}
            {editable && (
              <>
                {/* Row delete buttons (left of each content row, centered vertically) */}
                {Array.from({ length: visRows }).map((_, r) => {
                  const top = rowHeights.slice(0, r).reduce((a,b)=>a+b,0) + rowGaps.slice(0, r).reduce((a,b)=>a+b,0) + (rowHeights[r] ?? 0)/2;
                  return (
                    <div key={`row-controls-${r}`} className="absolute -translate-y-1/2 z-40" style={{ top, left: -12 }}>
                      <div className="flex items-center gap-1 -translate-x-full">
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-red-50 text-red-600 opacity-70 hover:opacity-100"
                          title={`Delete row #${r+1}`}
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); deleteRowAt(r); }}
                        >
                          <svg className="h-3.5 w-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        </button>
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-white text-gray-700 opacity-70 hover:opacity-100"
                          title={`Row #${r+1} settings`}
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); /* local to grid */ setTrackSettingsFor({ type: 'row', index: r, x: -16, y: top }); }}
                        >
                          <svg className="h-3.5 w-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0A1.65 1.65 0 0 0 9 3.09V3a2 2 0 1 1 4 0v.09c0 .67.39 1.27 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06c-.46.46-.6 1.14-.33 1.73.14.32.33.62.57.87.24.24.54.43.86.57.59.27 1.27.13 1.73-.33l.06-.06"/></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Column delete buttons (above each content column, centered horizontally) */}
                {Array.from({ length: visCols }).map((_, c) => {
                  const left = colTrackWidths.slice(0, c).reduce((a,b)=>a+b,0) + colGaps.slice(0, c).reduce((a,b)=>a+b,0) + (colTrackWidths[c] ?? 0)/2;
                  return (
                    <div key={`col-controls-${c}`} className="absolute -translate-x-1/2 z-40" style={{ left, top: -12 }}>
                      <div className="flex items-center gap-1 -translate-y-full">
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-red-50 text-red-600 opacity-70 hover:opacity-100"
                          title={`Delete column #${c+1}`}
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); deleteColAt(c); }}
                        >
                          <svg className="h-3.5 w-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        </button>
                        <button
                          className="h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-white text-gray-700 opacity-70 hover:opacity-100"
                          title={`Column #${c+1} settings"`}
                          onMouseDown={e => { e.stopPropagation(); }}
                          onClick={e => { e.preventDefault(); e.stopPropagation(); /* local to grid */ setTrackSettingsFor({ type: 'col', index: c, x: left, y: -16 }); }}
                        >
                          <svg className="h-3.5 w-3.5 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0A1.65 1.65 0 0 0 9 3.09V3a2 2 0 1 1 4 0v.09c0 .67.39 1.27 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06c-.46.46-.6 1.14-.33 1.73.14.32.33.62.57.87.24.24.54.43.86.57.59.27 1.27.13 1.73-.33l.06-.06"/></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Track settings popup */}
                {trackSettingsFor && (
                  <div className="absolute z-50" style={{ left: trackSettingsFor.x, top: trackSettingsFor.y }}>
                    <div className="rounded-xl border border-gray-200 bg-white shadow-lg p-3 w-56 -translate-x-1/2 -translate-y-1/2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-gray-900">{trackSettingsFor.type === 'row' ? `Row` : `Column`} settings</div>
                        <button className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-gray-50" onClick={()=>setTrackSettingsFor(null)} title="Close">
                          <svg className="h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
                        </button>
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm text-gray-700 flex items-center justify-between">
                          <span>Edge radius</span>
                          <span className="font-medium text-gray-900">
                            {Math.round((trackSettingsFor.type==='row' ? (rowRadiusByIndex[trackSettingsFor.index] ?? (rad || 0)) : (colRadiusByIndex[trackSettingsFor.index] ?? (rad || 0))))}px
                          </span>
                        </div>
                        <input
                          type="range" min={0} max={32}
                          value={(trackSettingsFor.type==='row' ? (rowRadiusByIndex[trackSettingsFor.index] ?? rad) : (colRadiusByIndex[trackSettingsFor.index] ?? rad))}
                          onChange={(e)=>{
                            const v = Math.max(0, Math.min(32, parseInt(e.target.value,10)||0));
                            const ts = Date.now();
                            if (trackSettingsFor.type==='row') {
                              setRowRadiusByIndex(prev => ({ ...prev, [trackSettingsFor.index]: v }));
                              setRowRadiusTsByIndex(prev => ({ ...prev, [trackSettingsFor.index]: ts }));
                            } else {
                              setColRadiusByIndex(prev => ({ ...prev, [trackSettingsFor.index]: v }));
                              setColRadiusTsByIndex(prev => ({ ...prev, [trackSettingsFor.index]: ts }));
                            }
                          }}
                          className="w-full accent-blue-600"
                        />
                        <div className="flex items-center justify-between pt-1">
                          <button
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 text-sm"
                            onClick={()=>{
                              if (trackSettingsFor.type==='row') {
                                setRowRadiusByIndex(prev => { const { [trackSettingsFor.index]:_, ...rest } = prev; return rest; });
                                setRowRadiusTsByIndex(prev => { const { [trackSettingsFor.index]:_, ...rest } = prev; return rest; });
                              } else {
                                setColRadiusByIndex(prev => { const { [trackSettingsFor.index]:_, ...rest } = prev; return rest; });
                                setColRadiusTsByIndex(prev => { const { [trackSettingsFor.index]:_, ...rest } = prev; return rest; });
                              }
                            }}
                          >
                            Reset
                          </button>
                          {trackSettingsFor.type==='col' && (
                            <div className="ml-auto text-[11px] text-gray-400 px-2">#{trackSettingsFor.index+1}</div>
                          )}
                        </div>
                        {trackSettingsFor.type==='col' && (
                          <>
                            <div className="h-px bg-gray-100 my-2" />
                            <div className="text-sm text-gray-700 flex items-center justify-between">
                              <span>Row spacing</span>
                              <span className="font-medium text-gray-900">{Math.round((rowGaps.length ? rowGaps.reduce((a,b)=>a+b,0)/rowGaps.length : gy))}px</span>
                            </div>
                            <input
                              type="range" min={0} max={96}
                              value={Math.round(rowGaps.length ? rowGaps.reduce((a,b)=>a+b,0)/rowGaps.length : gy)}
                              onChange={(e)=>{
                                const v = Math.max(0, Math.min(256, parseInt(e.target.value,10)||0));
                                setRowGaps(prev => prev.map(()=>v));
                              }}
                              className="w-full accent-blue-600"
                            />
                            <div className="flex items-center justify-between pt-1">
                              <button
                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 text-sm"
                                onClick={()=>{ setRowGaps(prev => prev.map(()=> gy)); }}
                              >
                                Reset spacing
                              </button>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-end pt-2">
                          <button
                            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm hover:from-blue-700 hover:to-indigo-700"
                            onClick={()=>setTrackSettingsFor(null)}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Core helper lines across content segments (visible even when gap is 0) */}
                {Array.from({ length: Math.max(0, visCols - 1) }).map((_, i) => (
                  <React.Fragment key={`vline-core-${i}`}>
                    {Array.from({ length: visRows }).map((_, r) => {
                      const left = vLineLefts[i] ?? 0;
                      const top = rowHeights.slice(0, r).reduce((a,b)=>a+b,0) + rowGaps.slice(0, r).reduce((a,b)=>a+b,0);
                      const height = rowHeights[r];
                      const inside = merges.some(m => r >= m.r && r < m.r + m.rs && i >= m.c && i < m.c + m.cs - 1);
                      if (inside && !showInnerHelpers) return null;
                      const isBlue = scaleMode?.type==='col'&&scaleMode.index===i;
                      const bg = isBlue ? 'rgba(147,197,253,0.8)' : (inside ? 'rgba(196,181,253,0.10)' : 'rgba(167,139,250,0.9)');
                      const px = 2;
                      return (
                        <div key={`vseg-core-${i}-${r}`} className="absolute" style={{ left, top, width: 0, height, zIndex: 30 }}>
                          <div className={"h-full cursor-col-resize rounded"} style={{ width: px, marginLeft: -(px/2), backgroundColor: bg }}
                               onMouseDown={(e)=>onDragStartLine(e,'col',i)}
                               onDoubleClick={() => setScaleMode(m => (m && m.type==='col' && m.index===i) ? null : { type:'col', index:i })}
                               title="Drag to resize columns" />
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {Array.from({ length: Math.max(0, visRows - 1) }).map((_, j) => (
                  <React.Fragment key={`hline-core-${j}`}>
                    {Array.from({ length: visCols }).map((_, c) => {
                      const top = hGapCenters[j] ?? 0;
                      const left = colTrackWidths.slice(0, c).reduce((a,b)=>a+b,0) + colGaps.slice(0, c).reduce((a,b)=>a+b,0);
                      const width = colTrackWidths[c] ?? 0;
                      const inside = merges.some(m => c >= m.c && c < m.c + m.cs && j >= m.r && j < m.r + m.rs - 1);
                      if (inside && !showInnerHelpers) return null;
                      const isBlue = scaleMode?.type==='row'&&scaleMode.index===j;
                      const bg = isBlue ? 'rgba(147,197,253,0.8)' : (inside ? 'rgba(196,181,253,0.10)' : 'rgba(167,139,250,0.9)');
                      const px = 2;
                      return (
                        <div key={`hseg-core-${j}-${c}`} className="absolute" style={{ left, top, width, height: 0, zIndex: 30 }}>
                          <div className={"w-full my-auto cursor-row-resize rounded"} style={{ height: px, marginTop: -(px/2), backgroundColor: bg }}
                               onMouseDown={(e)=>onDragStartLine(e,'row',j)}
                               onDoubleClick={() => setScaleMode(m => (m && m.type==='row' && m.index===j) ? null : { type:'row', index:j })}
                               title="Drag to resize rows" />
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {/* Special-case: when only one row exists, still show a horizontal resize line at the bottom of the first row */}
                {visRows === 1 && (
                  <React.Fragment key="hline-core-single">
                    {Array.from({ length: visCols }).map((_, c) => {
                      const top = (rowHeights[0] ?? 0) + 6;
                      const left = colTrackWidths.slice(0, c).reduce((a,b)=>a+b,0) + colGaps.slice(0, c).reduce((a,b)=>a+b,0);
                      const width = colTrackWidths[c] ?? 0;
                      const isBlue = scaleMode?.type==='row' && scaleMode.index===0;
                      const bg = isBlue ? 'rgba(147,197,253,0.8)' : 'rgba(167,139,250,0.9)';
                      const px = 2;
                      return (
                        <div key={`hseg-core-single-${c}`} className="absolute" style={{ left, top, width, height: 0, zIndex: 30 }}>
                          <div className={"w-full my-auto cursor-row-resize rounded"} style={{ height: px, marginTop: -(px/2), backgroundColor: bg }}
                               onMouseDown={(e)=>onDragStartLine(e,'row',0)}
                               onDoubleClick={() => setScaleMode(m => (m && m.type==='row' && m.index===0) ? null : { type:'row', index:0 })}
                               title="Drag to resize rows" />
                        </div>
                      );
                    })}
                  </React.Fragment>
                )}
                {/** Left handles for each horizontal helper line to adjust row gaps */}
                {showGapHandles && (
                  <>
                    {Array.from({ length: Math.max(0, visRows - 1) }).map((_, j) => {
                      const top = hGapCenters[j] ?? 0;
                      const rg = rowGaps[j] ?? gy;
                      return (
                        <button
                          key={`hline-left-handle-${j}`}
                          className="absolute h-6 min-w-[24px] px-2 -translate-y-1/2 rounded-full bg-white border border-gray-200 shadow cursor-ns-resize hover:bg-blue-50 flex items-center justify-center"
                          style={{ left: -12, top, zIndex: 31 }}
                          title={`Slide to set row gap (#${j+1})`}
                          onMouseDown={(e)=>onGapHandleDown(e,'row',j)}
                        >
                          <span className="text-[10px] leading-none text-gray-600 select-none text-center">{Math.round(rg)}</span>
                        </button>
                      );
                    })}
                  </>
                )}
                {/* Vertical helpers segmented per GAP (between rows) so the line sits inside the gap */}
                {Array.from({ length: Math.max(0, visCols - 1) }).map((_, i) => (
                  <React.Fragment key={`vline-${i}`}>
                    {Array.from({ length: Math.max(0, visRows - 1) }).map((_, j) => {
                      const left = vLineLefts[i] ?? 0;
                      const topGapStart = rowHeights.slice(0, j + 1).reduce((a,b)=>a+b,0) + rowGaps.slice(0, j).reduce((a,b)=>a+b,0);
                      const height = rowGaps[j] ?? gy;
                      const inside = merges.some(m => (i >= m.c && i < m.c + m.cs - 1) && (j >= m.r && j < m.r + m.rs - 1));
                      if (inside && !showInnerHelpers) return null;
                      const isBlue = scaleMode?.type==='col'&&scaleMode.index===i;
                      const bg = isBlue ? 'rgba(147,197,253,0.7)' : (inside ? 'rgba(196,181,253,0.10)' : 'rgba(196,181,253,0.68)');
                      const px = inside ? 1 : 2;
                      return (
                        <div key={`vseg-gap-${i}-${j}`} className="absolute" style={{ left, top: topGapStart, width: 0, height, zIndex: 30 }}>
                          <div className={`h-full cursor-col-resize rounded`} style={{ width: px, marginLeft: -(px/2), backgroundColor: bg }}
                               onMouseDown={(e)=>onDragStartLine(e,'col',i)}
                               onDoubleClick={() => setScaleMode(m => (m && m.type==='col' && m.index===i) ? null : { type:'col', index:i })}
                               title="Drag to resize columns" />
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {/* Bottom handle: allow resizing the last row by dragging below it */}
                {visRows > 1 && (
                  <React.Fragment key="hline-core-bottom">
                    {Array.from({ length: visCols }).map((_, c) => {
                      const top = totalGridHeight + 6;
                      const left = colTrackWidths.slice(0, c).reduce((a,b)=>a+b,0) + colGaps.slice(0, c).reduce((a,b)=>a+b,0);
                      const width = colTrackWidths[c] ?? 0;
                      const isBlue = scaleMode?.type==='row' && scaleMode.index===(visRows-1);
                      const bg = isBlue ? 'rgba(147,197,253,0.8)' : 'rgba(167,139,250,0.9)';
                      const px = 2;
                      return (
                        <div key={`hseg-core-bottom-${c}`} className="absolute" style={{ left, top, width, height: 0, zIndex: 30 }}>
                          <div className={"w-full my-auto cursor-row-resize rounded"} style={{ height: px, marginTop: -(px/2), backgroundColor: bg }}
                               onMouseDown={(e)=>onDragStartLine(e,'row',visRows-1)}
                               onDoubleClick={() => setScaleMode(m => (m && m.type==='row' && m.index===(visRows-1)) ? null : { type:'row', index:(visRows-1) })}
                               title="Drag to resize last row" />
                        </div>
                      );
                    })}
                  </React.Fragment>
                )}
                {/* Horizontal helpers segmented per GAP (between columns) so the line sits inside the gap */}
                {Array.from({ length: Math.max(0, visRows - 1) }).map((_, j) => (
                  <React.Fragment key={`hline-${j}`}>
                    {Array.from({ length: Math.max(0, visCols - 1) }).map((_, i) => {
                      const gapLeftStart = colTrackWidths.slice(0, i + 1).reduce((a,b)=>a+b,0) + colGaps.slice(0, i).reduce((a,b)=>a+b,0);
                      const topCenter = hGapCenters[j] ?? (rowHeights.slice(0, j + 1).reduce((a,b)=>a+b,0) + (rowGaps[j] ?? gy) / 2);
                      const width = colGaps[i] ?? gx;
                      const inside = merges.some(m => (i >= m.c && i < m.c + m.cs - 1) && (j >= m.r && j < m.r + m.rs - 1));
                      if (inside && !showInnerHelpers) return null;
                      const isBlue = scaleMode?.type==='row'&&scaleMode.index===j;
                      const bg = isBlue ? 'rgba(147,197,253,0.7)' : (inside ? 'rgba(196,181,253,0.10)' : 'rgba(196,181,253,0.68)');
                      const px = inside ? 1 : 2;
                      return (
                        <div key={`hseg-gap-${j}-${i}`} className="absolute" style={{ left: gapLeftStart, top: topCenter, width, height: 0, zIndex: 30 }}>
                          <div className={`w-full my-auto cursor-row-resize rounded`} style={{ height: px, marginTop: -(px/2), backgroundColor: bg }}
                               onMouseDown={(e)=>onDragStartLine(e,'row',j)}
                               onDoubleClick={() => setScaleMode(m => (m && m.type==='row' && m.index===j) ? null : { type:'row', index:j })}
                               title="Drag to resize rows" />
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                {/* Intersection dots */}
                {vLineLefts.map((x, i) => (
                  hGapCenters.map((y, j) => {
                    const insideDot = merges.some(m => (i >= m.c && i < m.c + m.cs - 1) && (j >= m.r && j < m.r + m.rs - 1));
                    if (insideDot && !showInnerHelpers) return null;
                    return (
                      <div>
                        <button
                          key={`dot-${i}-${j}`}
                          className="absolute rounded-full shadow-sm border border-white/70 bg-purple-300/70 hover:bg-blue-400 focus:outline-none"
                          style={{ left: `${x}px`, top: `${y}px`, width: 10, height: 10, marginLeft: -5, marginTop: -5, zIndex: 50, cursor: 'move' }}
                          title="Drag to resize row and column together"
                          onMouseEnter={() => setHoveredDot({ i, j, x, y })}
                          onMouseDown={(e)=>onDotDown(e, i, j)}
                        />
                        {hoveredDot && hoveredDot.i === i && hoveredDot.j === j && (
                          <>
                            {/* Add button to the left of the dot (edge 4px from dot) */}
                            <button
                              key={`dot-add-left-${i}-${j}`}
                              className="absolute h-6 w-6 -translate-y-1/2 -translate-x-full rounded-full bg-white border border-gray-200 shadow hover:bg-green-50"
                              style={{ left: x - 4, top: y, zIndex: 51 }}
                              title="Add"
                              onMouseEnter={() => setHoveredDot({ i, j, x, y })}
                              onMouseDown={(e)=>{ e.stopPropagation(); }}
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setMenuPos({ x: x + 12, y: y + 12, i, j }); }}
                            >
                              <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-green-600" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14"/>
                              </svg>
                            </button>
                            {/* Add button below the dot (edge 4px from dot) */}
                            <button
                              key={`dot-add-bottom-${i}-${j}`}
                              className="absolute h-6 w-6 -translate-x-1/2 rounded-full bg-white border border-gray-200 shadow hover:bg-green-50"
                              style={{ left: x, top: y + 4, zIndex: 51 }}
                              title="Add"
                              onMouseEnter={() => setHoveredDot({ i, j, x, y })}
                              onMouseDown={(e)=>{ e.stopPropagation(); }}
                              onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setMenuPos({ x: x + 12, y: y + 12, i, j }); }}
                            >
                              <svg viewBox="0 0 24 24" className="h-4 w-4 mx-auto text-green-600" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14"/>
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })
                ))}
                {/* Gap handles positioned near each dot (toggleable) */}
                {showGapHandles && (
                  vLineLefts.map((x, i) => (
                    hGapCenters.map((y, j) => {
                      const insideDot = merges.some(m => (i >= m.c && i < m.c + m.cs - 1) && (j >= m.r && j < m.r + m.rs - 1));
                      if (insideDot && !showInnerHelpers) return null;
                      const cg = colGaps[i] ?? gx; const rg = rowGaps[j] ?? gy;
                      return (
                        <React.Fragment key={`gap-handles-${i}-${j}`}>
                          <div
                            className="absolute h-3 w-10 -translate-x-1/2 -translate-y-full rounded-full bg-white border border-gray-200 shadow cursor-ns-resize hover:bg-blue-50"
                            style={{ left: x, top: y - 4 }}
                            title={`Slide to set row gap (#${j+1})`}
                            onMouseDown={(e)=>onGapHandleDown(e,'row',j)}
                          >
                            <div className="h-full w-full flex items-center justify-center text-[10px] text-gray-500 select-none">{Math.round(rg)}</div>
                          </div>
                          <div
                            className="absolute h-10 w-3 translate-x-full -translate-y-1/2 rounded-full bg-white border border-gray-200 shadow cursor-ew-resize hover:bg-blue-50"
                            style={{ left: x + 4, top: y }}
                            title={`Slide to set column gap (#${i+1})`}
                            onMouseDown={(e)=>onGapHandleDown(e,'col',i)}
                          >
                            <div className="h-full w-full flex items-center justify-center rotate-90 text-[10px] text-gray-500 select-none">{Math.round(cg)}</div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  ))
                )}
              </>
            )}
          </div>
          {/* Context menu for '+' actions */}
          {menuPos && (
            <div
              className="absolute z-[100] mt-2 w-44 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 border border-gray-100"
              style={{ left: menuPos.x, top: menuPos.y }}
              onMouseDown={e => e.stopPropagation()}
           >
              <button
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => { if (menuPos) insertRowAtGap(menuPos.j); setMenuPos(null); }}
              >
                Insert Row
              </button>
              <button
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => { if (menuPos) insertColAtGap(menuPos.i); setMenuPos(null); }}
              >
                Insert Column
              </button>
            </div>
          )}
          {page.blocks.length === 0 && (cols * rws === 1) && !editable && !compact && (
            <div className="text-center text-gray-400 py-8">This page is empty. Use the toolbar to add rows/columns and add blocks.</div>
          )}
        </div>
        <ListEditor
          open={!!listEditorFor}
          orientation={(listEditorFor?.orientation || 'vertical') as 'vertical'|'horizontal'}
          initialItems={(listEditorFor && (page.blocks.find(b=>b.id===listEditorFor.id) as any)?.listItems) || []}
          initialEntries={(listEditorFor && (page.blocks.find(b=>b.id===listEditorFor.id) as any)?.listEntries) || []}
          initialImageSize={(listEditorFor && (page.blocks.find(b=>b.id===listEditorFor.id) as any)?.listImageSize) || undefined}
          initialImageRadius={(listEditorFor && (page.blocks.find(b=>b.id===listEditorFor.id) as any)?.listImageRadius) || undefined}
          onClose={()=> setListEditorFor(null)}
          onSave={(items, entries, opts)=>{ const t = listEditorFor; setListEditorFor(null); if (t && onUpdateBlock) onUpdateBlock(t.id, { listItems: items, listEntries: entries, listImageSize: opts.imageSize, listImageRadius: opts.imageRadius }); }}
        />
        <MediaPickerModal
          open={!!mediaPickerFor}
          onClose={() => setMediaPickerFor(null)}
          onSelect={(item) => {
            const t = mediaPickerFor; setMediaPickerFor(null); setSettingsFor(null);
            if (t && onUpdateBlock) onUpdateBlock(t.id, { url: item.url });
          }}
        />
      </div>
    </div>
  );
};

export default DisplayPage;
